import React, { useEffect, useState, useCallback } from "react";
import { gcRoiAPI } from "../../services/api";
import GcRoiDrillDrawer from "./GcRoiDrillDrawer";
import GcRoiIntroductionActionModal from "./GcRoiIntroductionActionModal";
import GcRoiIntroductionWorkflowTracker from "./GcRoiIntroductionWorkflowTracker";
import {
  createdByDisplayLabel,
  metricContextLabel,
  outreachCopy,
  progressPercent,
  projectContextLabel,
  queueAddErrorMessage,
  queuePrimaryLabel,
  type QueueWorkflowItem,
} from "./gcRoiIntroductionWorkflow";
import { auth } from "../../firebase";

type TradePartnerMatch = {
  vendorId: string;
  companyName: string;
  trancheLabels?: string[];
};

interface GcRoiSummary {
  gcOrgId: string;
  metrics: {
    bidParticipationRate: number;
    bidDensity: number;
    riskWeightedParticipation: number;
    avgBidValueCoverage: number;
  };
  cards: {
    renewalDueVendors: number;
    renewalDueActiveBidders: number;
    renewalDueActiveBidderPct: number;
    atRiskVendors: number;
    liquidityRiskActiveBidders: number;
    liquidityRiskActiveBidderPct: number;
    suretyBackedActiveBidders: number;
    suretyBackedActiveBidderPct: number;
    renewalAndLiquidityActiveBidders: number;
    renewalAndLiquidityActiveBidderPct: number;
    totalSuretyAggregateCapacity: number;
    totalSuretySingleCapacity: number;
  };
  matchedTotal: number;
  prequalifiedTotal: number;
  totalMatchedBids: number;
  totalProjects: number | null;
  totalProjectsStatus?: string;
  totalOpportunities: number | null;
  totalOpportunitiesStatus?: string;
  readiness?: {
    verifiedReady: number;
    reviewRecommended: number;
    readinessUnknown: number;
    sourcePending: number;
    verifiedReadyStatus?: string;
    reviewRecommendedStatus?: string;
    classifierAvailable?: boolean;
    withFinancialEvidence?: number;
    negativeWorkingCapital?: number;
    liquidityRisk?: number;
    suretyCapacityOnFile?: number;
    note?: string;
  };
  portfolio?: {
    projectConcentration?: ProjectConcentrationRow[];
    projectConcentrationStatus?: string;
    regionStatus?: string;
    projectTypeStatus?: string;
    growthConcentration?: GrowthConcentrationRow[];
    growthConcentrationStatus?: string;
  };
  financialRmp?: {
    projectApprovalCount?: number;
    approvedWithRmpCount?: number;
    approvedWithRmpLiquidityRiskCount?: number;
    approvedWithRmpLiquidityRiskTradePartners?: number;
    approvedWithRmpProjectCount?: number;
    approvedWithRmpTradePartnerCount?: number;
    classificationStatus?: string;
    historicalRmpAwardValue?: number | string | null;
    historicalRmpAwardLabel?: string;
    currentActiveExposureStatus?: string;
    resolvedRmpCount?: number;
    unresolvedRmpCount?: number;
    resolvedBiddingRmpCount?: number;
    resolvedActiveExposure?: number | string | null;
    unresolvedHistoricalAwardValue?: number | string | null;
  };
  actionCenter?: ActionCenterCard[];
}

interface ActionCenterCard {
  id: string;
  family: string;
  title: string;
  signal: string;
  count: number;
  whyThisMatters?: string;
  whyShown?: string;
  context?: string;
  cta: string;
  metricKey: string;
}

interface ProjectConcentrationRow {
  matchedProjectId?: string | null;
  projectName?: string | null;
  approvalCount?: number;
  approvedWithRmpCount?: number;
  knownAwardValue?: number | string | null;
  approvedWithRmpAwardValue?: number | string | null;
  tradePartnerCount?: number;
  approvedWithRmpTradePartnerCount?: number;
  approvedWithRmpLiquidityRiskCount?: number;
  approvedWithRmpLiquidityRiskRows?: number;
  flaggedTradePartnerCount?: number;
}

interface GrowthConcentrationRow {
  projectId?: string | null;
  projectName?: string | null;
  bidderCount?: number;
  bidCount?: number;
}

type EvidenceFilter =
  | "all"
  | "requires-attention"
  | "approved-rmp"
  | "approved-rmp-liquidity"
  | "liquidity-risk"
  | "renewal-due";
type WorkspaceView =
  | "overview"
  | "portfolio-intelligence"
  | "grow"
  | "protect"
  | "introduction-queue";

const WORKSPACE_VIEWS: { id: WorkspaceView; label: string }[] = [
  { id: "overview", label: "Action Center" },
  { id: "introduction-queue", label: "Introduction Queue" },
  { id: "portfolio-intelligence", label: "Portfolio Intelligence" },
  { id: "grow", label: "Grow" },
  { id: "protect", label: "Protect" },
];

function viewFromHash(hash: string): WorkspaceView {
  const id = String(hash || "").replace(/^#/, "");
  return WORKSPACE_VIEWS.some((view) => view.id === id) ? (id as WorkspaceView) : "overview";
}

const S: Record<string, React.CSSProperties> = {
  shell: {
    display: "grid",
    gridTemplateColumns: "220px minmax(0, 1fr)",
    minHeight: "100vh",
    backgroundColor: "#080b10",
    color: "#f2f5f8",
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 14,
    lineHeight: 1.45,
  },
  sidebar: {
    position: "sticky",
    top: 0,
    height: "100vh",
    padding: "22px 16px",
    borderRight: "1px solid #25303d",
    backgroundColor: "#0b0f15",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    margin: "0 8px 28px",
    fontWeight: 750,
    letterSpacing: "-0.02em",
  },
  brandMark: {
    display: "grid",
    width: 28,
    height: 28,
    placeItems: "center",
    borderRadius: 8,
    backgroundColor: "#70e1b2",
    color: "#052016",
    fontWeight: 900,
  },
  nav: { display: "grid", gap: 5 },
  navLink: {
    padding: "9px 10px",
    borderRadius: 7,
    color: "#8d9aaa",
    textDecoration: "none",
    backgroundColor: "transparent",
    border: "none",
    textAlign: "left",
    cursor: "pointer",
    font: "inherit",
  },
  navLinkActive: {
    color: "#f2f5f8",
    backgroundColor: "#151c26",
  },
  pathwayAction: {
    margin: "10px 0 0",
    color: "#7eb6ff",
    fontSize: 12,
    fontWeight: 700,
  },
  main: { width: "min(1460px, 100%)", padding: "22px 28px 54px" },
  topbar: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    marginBottom: 22,
  },
  eyebrow: {
    marginBottom: 4,
    color: "#70e1b2",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  title: { margin: "0 0 4px", fontSize: "clamp(23px, 3vw, 32px)", letterSpacing: "-0.035em" },
  subtitle: { margin: 0, color: "#8d9aaa" },
  logout: {
    background: "none",
    border: "1px solid #25303d",
    borderRadius: "0.375rem",
    color: "#8d9aaa",
    cursor: "pointer",
    fontSize: "0.75rem",
    padding: "0.375rem 0.75rem",
    whiteSpace: "nowrap",
  },
  section: { scrollMarginTop: 18, marginBottom: 28 },
  sectionHead: { margin: "0 0 4px", fontSize: 17, letterSpacing: "-0.015em" },
  sectionCopy: { margin: "0 0 10px", color: "#8d9aaa", fontSize: 12 },
  metricGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 },
  actionGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10, margin: "0 0 18px" },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 },
  threeCol: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 },
  card: {
    minWidth: 0,
    padding: 15,
    border: "1px solid #25303d",
    borderRadius: 9,
    backgroundColor: "#10151d",
  },
  metric: { minHeight: 104, display: "flex", flexDirection: "column", justifyContent: "space-between" },
  metricLabel: { color: "#8d9aaa", fontSize: 12, margin: 0 },
  metricValue: { fontSize: "clamp(19px, 2vw, 26px)", fontWeight: 760, letterSpacing: "-0.03em", margin: "8px 0" },
  metricNote: { color: "#8d9aaa", fontSize: 11, margin: 0 },
  pendingValue: { color: "#f3c56b" },
  positiveValue: { color: "#70e1b2" },
  riskValue: { color: "#ff8f8f" },
  intelCard: { minHeight: 156 },
  intelTitle: {
    margin: "0 0 14px",
    fontSize: 12,
    color: "#8d9aaa",
    fontWeight: 650,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  intelValue: { fontSize: 18, fontWeight: 720, margin: 0 },
  intelNote: { margin: "8px 0 0", color: "#8d9aaa", fontSize: 12 },
  pendingBlock: {
    padding: "18px 10px",
    border: "1px dashed #4c4434",
    borderRadius: 7,
    color: "#f3c56b",
    textAlign: "center",
  },
  selector: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "0.65rem" },
  selectorBtn: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.25rem",
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.55rem",
    color: "#d5dbe2",
    cursor: "pointer",
    fontSize: "0.75rem",
    fontWeight: 700,
    padding: "0.65rem 0.75rem",
    textAlign: "left",
  },
  pendingTag: { color: "#f3c56b", fontSize: "0.68rem", fontStyle: "italic", fontWeight: 700 },
  drillHint: {
    color: "#7eb6ff",
    fontSize: "0.65rem",
    fontWeight: 600,
    margin: "0.35rem 0 0 0",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  exposure: {
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.55rem",
    marginTop: "0.85rem",
    padding: "0.75rem",
  },
  queueRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "stretch",
    justifyContent: "space-between",
    padding: "0.9rem 1rem",
    backgroundColor: "#10151d",
    borderRadius: 8,
    border: "1px solid #25303d",
    marginBottom: "0.55rem",
    gap: "0.9rem",
  },
  queueName: { fontSize: "0.9rem", fontWeight: 720, color: "#f2f5f8", margin: "0 0 0.2rem 0" },
  queueMeta: { fontSize: "0.72rem", color: "#8d9aaa", margin: "0 0 0.55rem 0" },
  queuePolicy: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))",
    gap: "0.75rem 1rem",
    marginTop: "0.65rem",
    alignItems: "start",
  },
  queueField: { color: "#d5dbe2", fontSize: "0.78rem", lineHeight: 1.45, margin: 0, minWidth: 0, overflowWrap: "anywhere" },
  queueNotes: {
    minWidth: 0,
    marginTop: "0.75rem",
    paddingTop: "0.65rem",
    borderTop: "1px solid #25303d",
  },
  notePreview: {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
    overflowWrap: "anywhere" as const,
    wordBreak: "break-word" as const,
    whiteSpace: "pre-wrap" as const,
    maxHeight: "4.35em",
    lineHeight: 1.45,
    margin: 0,
    color: "#d5dbe2",
    fontSize: "0.78rem",
  },
  queueLabel: {
    color: "#8d9aaa",
    display: "block",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    marginBottom: "0.15rem",
    textTransform: "uppercase",
  },
  queueRight: { display: "flex", alignItems: "stretch", flexDirection: "column", gap: "0.45rem", minWidth: "11.5rem" },
  removeBtn: {
    background: "none",
    border: "1px solid #25303d",
    borderRadius: "0.375rem",
    color: "#8d9aaa",
    cursor: "pointer",
    fontSize: "0.7rem",
    padding: "0.2rem 0.5rem",
  },
  statusBadge: {
    borderRadius: "999px",
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "0.28rem 0.65rem",
    backgroundColor: "#182231",
    color: "#7eb6ff",
    border: "1px solid #3a4b5e",
    display: "inline-block",
    userSelect: "none",
  },
  ownerPill: {
    display: "inline-block",
    borderRadius: "999px",
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "0.22rem 0.55rem",
    backgroundColor: "#0b0f15",
    color: "#e2e8f0",
    border: "1px solid #3a4b5e",
  },
  progressTrack: {
    display: "block",
    width: "100%",
    maxWidth: "100%",
    height: 6,
    borderRadius: 999,
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    overflow: "hidden",
    marginTop: "0.35rem",
    boxSizing: "border-box",
  },
  progressFill: { display: "block", height: "100%", maxWidth: "100%", backgroundColor: "#7eb6ff", borderRadius: 999 },
  primaryBtn: {
    backgroundColor: "#1d4ed8",
    border: "1px solid #1d4ed8",
    borderRadius: "0.45rem",
    color: "#f8fafc",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontWeight: 700,
    padding: "0.45rem 0.7rem",
  },
  secondaryBtn: {
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.45rem",
    color: "#d5dbe2",
    cursor: "pointer",
    fontSize: "0.72rem",
    fontWeight: 700,
    padding: "0.45rem 0.7rem",
  },
  emptyQ: { color: "#8d9aaa", fontSize: "0.85rem", padding: "1rem 0", fontStyle: "italic" },
  sourceNote: { margin: "14px 0 0", color: "#8d9aaa", fontSize: 11 },
  searchInput: {
    width: "100%",
    boxSizing: "border-box" as const,
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.4rem",
    color: "#e2e8f0",
    font: "inherit",
    fontSize: "0.78rem",
    padding: "0.45rem 0.6rem",
  },
  fieldLabel: {
    color: "#8d9aaa",
    display: "block",
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    marginBottom: "0.25rem",
    textTransform: "uppercase" as const,
  },
  quickAddPanel: {
    backgroundColor: "#10151d",
    border: "1px solid #25303d",
    borderRadius: 8,
    marginBottom: "0.85rem",
    padding: "0.85rem 1rem",
  },
  searchResultBtn: {
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.4rem",
    color: "#d5dbe2",
    cursor: "pointer",
    display: "block",
    fontSize: "0.75rem",
    marginTop: "0.35rem",
    padding: "0.45rem 0.6rem",
    textAlign: "left" as const,
    width: "100%",
  },
  searchResultBtnSelected: {
    borderColor: "#7eb6ff",
    color: "#f2f5f8",
  },
  manualBadge: {
    borderRadius: "999px",
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "0.28rem 0.65rem",
    backgroundColor: "#1a2010",
    color: "#c8e08a",
    border: "1px solid #3f4d2a",
    display: "inline-block",
    marginLeft: "0.4rem",
  },
  sourcePendingBadge: {
    borderRadius: "999px",
    fontSize: "0.68rem",
    fontWeight: 700,
    padding: "0.28rem 0.65rem",
    backgroundColor: "#2a2110",
    color: "#f0c674",
    border: "1px solid #5a4a22",
    display: "inline-block",
    marginLeft: "0.4rem",
  },
  sourcePendingPanel: {
    backgroundColor: "#16130b",
    border: "1px solid #5a4a22",
    borderRadius: 8,
    margin: "0.35rem 0 0.55rem",
    padding: "0.55rem 0.75rem",
  },
  errorText: { color: "#ff8f8f", fontSize: "0.75rem", margin: "0.4rem 0 0" },
  barBtn: {
    display: "block",
    width: "100%",
    backgroundColor: "#0b0f15",
    border: "1px solid #25303d",
    borderRadius: "0.55rem",
    color: "inherit",
    cursor: "pointer",
    font: "inherit",
    padding: "0.7rem 0.8rem",
    textAlign: "left" as const,
  },
  barTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: "#080b10",
    border: "1px solid #25303d",
    overflow: "hidden",
    marginTop: 8,
  },
  barFill: {
    height: "100%",
    backgroundColor: "#70e1b2",
    borderRadius: 999,
  },
  filterRow: { display: "flex", flexWrap: "wrap" as const, gap: 8, margin: "8px 0 10px" },
  badge: {
    display: "inline-block",
    borderRadius: 999,
    fontSize: "0.62rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    padding: "0.18rem 0.5rem",
    marginRight: 6,
    backgroundColor: "#182231",
    color: "#ff8f8f",
    border: "1px solid #3a4b5e",
  },
};

const formatFixed = (value: unknown, digits = 1, fallback = "—"): string => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num.toFixed(digits) : fallback;
};

const formatBillions = (value: number | null | undefined) => {
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? `$${(num / 1000000000).toFixed(1)}B` : "—";
};

function sourcePendingCount(value: number | null | undefined, status?: string): boolean {
  return typeof value !== "number" || status === "SOURCE_PENDING";
}

function approvalLabel(item: QueueWorkflowItem): string {
  const status = String(item.workflow_stage || item.status || "").toUpperCase();
  if (["GC_INTRODUCTION_APPROVED", "INTRODUCTION_DRAFT_READY", "INTRODUCTION_SENT", "TRADE_PARTNER_ENGAGED", "SESSION_SCHEDULED"].includes(status)) {
    return "GC Approved";
  }
  if (status === "AWAITING_GC_INTRODUCTION_APPROVAL") return "Approval Pending";
  return "Not yet approved";
}

function pathwaySignal(metricKey: string | undefined): string {
  const key = String(metricKey || "").toUpperCase();
  const label = metricContextLabel(metricKey);
  if (/BONDREADY/.test(key)) return `BondReady · ${label}`;
  if (/(AT_RISK|LIQUIDITY|RENEWAL|SURETY|APPROVED_RMP)/.test(key)) return `Protect · ${label}`;
  if (/(NEGATIVE|FINANCIAL|MATCHED|BID|PARTICIP|CAPITAL)/.test(key)) return `Grow · ${label}`;
  return label;
}

function timelineLabel(item: QueueWorkflowItem): string | undefined {
  if (!item.created_at) return undefined;
  return `Created ${item.created_at}`;
}

const NOTE_PREVIEW_LINES = 3;
const NOTE_PREVIEW_LINE_LENGTH = 140;
const NOTE_PREVIEW_ELLIPSIS = "...";

function notesPreview(notes: string): string {
  const lines = notes.split(/\r?\n/);
  const hasMoreLines = lines.length > NOTE_PREVIEW_LINES;
  return lines.slice(0, NOTE_PREVIEW_LINES).map((line, index, shown) => {
    const shorten = line.length > NOTE_PREVIEW_LINE_LENGTH;
    const markMore = hasMoreLines && index === shown.length - 1;
    if (!shorten && !markMore) return line;
    const budget = NOTE_PREVIEW_LINE_LENGTH - NOTE_PREVIEW_ELLIPSIS.length;
    return `${line.slice(0, Math.max(0, budget)).replace(/\s+$/, "")}${NOTE_PREVIEW_ELLIPSIS}`;
  }).join("\n");
}

interface ProjectApproval {
  id?: string | null;
  matchedProjectId?: string | null;
  projectName?: string | null;
  approvalResponse?: string | null;
  approvedWithRmp?: boolean;
  subcontractAwardValue?: number | string | null;
  projectLocation?: string | null;
  projectDuration?: string | null;
  tradeType?: string | null;
  rmpRequirements?: string[] | null;
  sourceSystem?: string | null;
  sourceEventAt?: string | null;
  rmpClassificationStatus?: string | null;
  rmpClassification?: string | null;
  activityStatus?: string | null;
  vendorId?: string | null;
  tradePartner?: string | null;
  liquidityRisk?: boolean | null;
  renewalDue?: boolean | null;
  totalBids?: number | string | null;
}

interface ProjectApprovalRow extends ProjectApproval {
  tradePartner: string;
  vendorId: string;
}

function mapApprovalRow(row: any, extras?: { tradePartner?: string; vendorId?: string }): ProjectApprovalRow {
  return {
    id: row.id ?? null,
    matchedProjectId: row.matchedProjectId ?? null,
    tradePartner: String(extras?.tradePartner || row.tradePartner || row.companyName || "Unnamed trade partner"),
    vendorId: String(extras?.vendorId || row.vendorId || ""),
    projectName: row.projectName ?? null,
    approvalResponse: row.approvalResponse ?? null,
    approvedWithRmp: Boolean(row.approvedWithRmp),
    subcontractAwardValue: row.subcontractAwardValue ?? null,
    projectLocation: row.projectLocation ?? null,
    projectDuration: row.projectDuration ?? null,
    tradeType: row.tradeType ?? null,
    rmpRequirements: Array.isArray(row.rmpRequirements) ? row.rmpRequirements : [],
    sourceSystem: row.sourceSystem ?? null,
    sourceEventAt: row.sourceEventAt ?? null,
    rmpClassificationStatus: row.rmpClassificationStatus ?? null,
    rmpClassification: row.rmpClassification ?? null,
    activityStatus: row.activityStatus ?? null,
    liquidityRisk: row.liquidityRisk === true,
    renewalDue: row.renewalDue === true,
    totalBids: row.totalBids ?? null,
  };
}

function flattenProjectApprovals(payload: any): ProjectApprovalRow[] {
  if (Array.isArray(payload)) {
    return payload
      .filter((row) => row && (row.projectName || row.approvalResponse || row.tradePartner || row.companyName))
      .map((row) => mapApprovalRow(row))
      .sort((a, b) => Number(b.approvedWithRmp) - Number(a.approvedWithRmp));
  }
  if (!Array.isArray(payload?.records)) return [];
  const nested = payload.records.some((record: any) => Array.isArray(record?.projectApprovals));
  if (!nested) return flattenProjectApprovals(payload.records);
  const rows: ProjectApprovalRow[] = [];
  payload.records.forEach((record: any) => {
    const approvals = Array.isArray(record?.projectApprovals) ? record.projectApprovals : [];
    approvals.forEach((approval: ProjectApproval) => {
      rows.push(mapApprovalRow(approval, {
        tradePartner: record?.companyName || approval.tradePartner,
        vendorId: record?.vendorId || approval.vendorId,
      }));
    });
  });
  return rows.sort((a, b) => Number(b.approvedWithRmp) - Number(a.approvedWithRmp));
}

function formatAward(value: number | string | null | undefined): string | null {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(num);
}

function numericValue(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  return Number.isFinite(num) ? num : null;
}

function resolvedActivityLabel(status?: string | null): string | null {
  const normalized = String(status || "").toUpperCase();
  if (normalized === "ACTIVE" || normalized === "BIDDING" || normalized === "INACTIVE") return normalized;
  return null;
}

function rowRequiresAttention(row: ProjectApprovalRow): boolean {
  return row.liquidityRisk === true || Boolean(row.approvedWithRmp) || row.renewalDue === true;
}

function rowMatchesProjectScope(
  row: ProjectApprovalRow,
  projectId?: string | null,
  projectName?: string | null,
): boolean {
  if (projectId) {
    return row.matchedProjectId != null && String(row.matchedProjectId) === String(projectId);
  }
  if (projectName) {
    return String(row.projectName || "") === projectName;
  }
  return true;
}

function resolveProjectIdFromApprovals(
  projectName: string,
  rows: ProjectApprovalRow[] | null,
): string | null {
  const ids = Array.from(new Set(
    (rows || [])
      .filter((row) => String(row.projectName || "") === projectName && row.matchedProjectId)
      .map((row) => String(row.matchedProjectId)),
  ));
  return ids.length === 1 ? ids[0] : null;
}

function countForFilter(rows: ProjectApprovalRow[], filter: EvidenceFilter): number {
  return rows.filter((row) => rowMatchesEvidenceFilter(row, filter)).length;
}

function rowMatchesEvidenceFilter(row: ProjectApprovalRow, filter: EvidenceFilter): boolean {
  if (filter === "approved-rmp") return Boolean(row.approvedWithRmp);
  if (filter === "approved-rmp-liquidity") return Boolean(row.approvedWithRmp) && row.liquidityRisk === true;
  if (filter === "liquidity-risk") return row.liquidityRisk === true;
  if (filter === "renewal-due") return row.renewalDue === true;
  if (filter === "requires-attention") return rowRequiresAttention(row);
  return true;
}

function defaultPartnerFilter(_row?: ProjectConcentrationRow | null): EvidenceFilter {
  return "requires-attention";
}

function evidenceReasonLabel(filter: EvidenceFilter): string {
  if (filter === "requires-attention") return "Requires Attention";
  if (filter === "liquidity-risk") return "Liquidity Risk";
  if (filter === "approved-rmp-liquidity") return "Approved W/RMP + Liquidity Risk";
  if (filter === "approved-rmp") return "Approved W/RMP";
  if (filter === "renewal-due") return "Renewal Due";
  return "Project relationship";
}

function queueMetricForEvidenceReason(filter: EvidenceFilter): string {
  if (filter === "requires-attention") return "PROJECT_TRADE_PARTNER_REVIEW";
  if (filter === "liquidity-risk") return "LIQUIDITY_RISK_ACTIVE_BIDDERS";
  if (filter === "approved-rmp-liquidity") return "APPROVED_RMP_LIQUIDITY";
  if (filter === "approved-rmp") return "APPROVED_RMP";
  if (filter === "renewal-due") return "RENEWAL_DUE_VENDORS";
  return "PROJECT_TRADE_PARTNER_REVIEW";
}

function FilterChip({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-pressed={active}
      onClick={onClick}
      style={{ ...S.secondaryBtn, borderColor: active ? "#7eb6ff" : "#25303d" }}
    >
      {label}
    </button>
  );
}

function recommendedPathForApproval(row: ProjectApprovalRow): { label: string; note: string } | null {
  const requirements = row.rmpRequirements || [];
  const bond = requirements.some((text) =>
    /bond|surety|credit support|letter of credit/i.test(String(text || ""))
  );
  if (bond) {
    return {
      label: "BondReady",
      note: "Bond-related RMP evidence supports a BondReady review. This does not mean an introduction occurred.",
    };
  }
  if (row.liquidityRisk === true) {
    return {
      label: "Capital Readiness",
      note: "Source-provided liquidity-risk evidence supports a capital-readiness review.",
    };
  }
  return null;
}

const RankedBarRow = ({
  title,
  valueLabel,
  ratio,
  meta,
  selected,
  onClick,
  testId,
  actionLabel,
  projectName,
}: {
  title: string;
  valueLabel: string;
  ratio: number;
  meta: string;
  selected?: boolean;
  onClick: () => void;
  testId: string;
  actionLabel?: string;
  projectName?: string;
}): React.ReactElement => {
  const width = Number.isFinite(ratio) && ratio > 0 ? Math.min(100, Math.max(ratio * 100, 2)) : 0;
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = Boolean(selected) || hovered || focused;
  return (
    <button
      type="button"
      data-testid={testId}
      data-project-name={projectName || title}
      aria-pressed={Boolean(selected)}
      aria-label={actionLabel ? `${actionLabel.replace(/[→>]/g, "").trim()} for ${title}` : title}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        ...S.barBtn,
        borderColor: active ? "#7eb6ff" : "#25303d",
        backgroundColor: selected ? "#151c26" : hovered || focused ? "#151c26" : "#0b0f15",
        outline: focused ? "2px solid #7eb6ff" : "none",
        outlineOffset: 2,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
        <p style={{ ...S.queueName, margin: 0 }}>{title}</p>
        <p style={{ ...S.intelValue, margin: 0, fontSize: 16 }}>{valueLabel}</p>
      </div>
      <p style={{ ...S.queueMeta, margin: "4px 0 0" }}>{meta}</p>
      <div style={S.barTrack} aria-hidden="true">
        <div data-testid={`${testId}-fill`} style={{ ...S.barFill, width: `${width}%`, backgroundColor: width ? "#70e1b2" : "transparent" }} />
      </div>
      {actionLabel ? (
        <p data-testid={`${testId}-action`} style={{ ...S.drillHint, marginTop: 8 }}>{actionLabel}</p>
      ) : null}
    </button>
  );
};

function ProjectApprovalEvidenceList({
  rows,
  loading,
  emptyCopy,
  projectId,
  projectName,
  includeRenewalFilter = false,
  includeProjectFilter = false,
  defaultFilter = "requires-attention",
  title = "Financial RMP / approval evidence",
  onOpenVendor,
  vendorActionLabel = "Open vendor detail",
  showRecommendedPath = false,
}: {
  rows: ProjectApprovalRow[] | null;
  loading: boolean;
  emptyCopy: string;
  projectId?: string | null;
  projectName?: string | null;
  includeRenewalFilter?: boolean;
  includeProjectFilter?: boolean;
  defaultFilter?: EvidenceFilter;
  title?: string;
  onOpenVendor?: (row: ProjectApprovalRow, reason: EvidenceFilter) => void;
  vendorActionLabel?: string;
  showRecommendedPath?: boolean;
}): React.ReactElement {
  const [filter, setFilter] = useState<EvidenceFilter>(defaultFilter);
  const [projectFilter, setProjectFilter] = useState<string>("");

  useEffect(() => {
    setFilter(defaultFilter);
  }, [defaultFilter, projectId, projectName]);

  const scoped = (rows || []).filter((row) => {
    if (projectId) {
      return rowMatchesProjectScope(row, projectId, null);
    }
    if (projectName) {
      return rowMatchesProjectScope(row, null, projectName);
    }
    if (projectFilter && String(row.projectName || "") !== projectFilter) return false;
    return true;
  });
  const visible = scoped.filter((row) => rowMatchesEvidenceFilter(row, filter));
  const projectOptions = Array.from(new Set((rows || []).map((row) => String(row.projectName || "")).filter(Boolean))).sort();
  const filterOptions: { id: EvidenceFilter; label: string; testId: string }[] = [
    { id: "requires-attention", label: `Requires Attention (${countForFilter(scoped, "requires-attention")})`, testId: "gc-roi-filter-requires-attention" },
    { id: "all", label: `All relevant trade partners (${countForFilter(scoped, "all")})`, testId: "gc-roi-filter-all" },
    { id: "liquidity-risk", label: `Liquidity Risk (${countForFilter(scoped, "liquidity-risk")})`, testId: "gc-roi-filter-liquidity-risk" },
    { id: "approved-rmp", label: `Approved W/RMP (${countForFilter(scoped, "approved-rmp")})`, testId: "gc-roi-filter-approved-rmp" },
    { id: "approved-rmp-liquidity", label: `Approved W/RMP + Liquidity Risk (${countForFilter(scoped, "approved-rmp-liquidity")})`, testId: "gc-roi-filter-approved-rmp-liquidity" },
  ];

  return (
    <article data-testid="gc-roi-project-approval-evidence" style={{ ...S.card, marginTop: 10 }}>
      <h3 style={S.intelTitle}>{title}</h3>
      <p style={S.intelNote}>
        Canonical TradeTapp closed-approval rows. Historical award values are not current exposure.
      </p>
      {loading || rows == null ? (
        <p style={S.emptyQ}>Loading project-approval evidence...</p>
      ) : rows.length === 0 ? (
        <p data-testid="gc-roi-project-approval-empty" style={S.emptyQ}>{emptyCopy}</p>
      ) : (
        <>
          <div role="group" aria-label="Approval evidence filters" style={S.filterRow}>
            {filterOptions.map((option) => (
              <React.Fragment key={option.id}>
                <FilterChip
                  label={option.label}
                  active={filter === option.id}
                  testId={option.testId}
                  onClick={() => setFilter(option.id)}
                />
              </React.Fragment>
            ))}
            {includeRenewalFilter && (
              <FilterChip
                label="Renewal Due"
                active={filter === "renewal-due"}
                testId="gc-roi-filter-renewal-due"
                onClick={() => setFilter("renewal-due")}
              />
            )}
            {includeProjectFilter && projectOptions.length > 0 && (
              <label style={{ ...S.metricNote, display: "flex", alignItems: "center", gap: 6 }}>
                Project
                <select
                  data-testid="gc-roi-filter-project"
                  value={projectFilter}
                  onChange={(event) => setProjectFilter(event.target.value)}
                  style={{ ...S.secondaryBtn, color: "#f2f5f8" }}
                >
                  <option value="">All projects</option>
                  {projectOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <p style={S.metricNote}>
            {visible.length.toLocaleString()} of {scoped.length.toLocaleString()} source-backed approval rows
          </p>
          {visible.length === 0 ? (
            <p data-testid="gc-roi-project-approval-empty-filter" style={S.emptyQ}>
              No approval rows match this filter.
            </p>
          ) : (
          <div data-testid="gc-roi-project-approval-list" style={{ maxHeight: 320, overflow: "auto", display: "grid", gap: 8 }}>
            {visible.map((row, index) => {
              const award = formatAward(row.subcontractAwardValue);
              const requirements = row.rmpRequirements || [];
              const activity = resolvedActivityLabel(row.activityStatus);
              const classification = row.rmpClassification ? String(row.rmpClassification).trim() : "";
              const sourceBits = [row.sourceSystem, row.sourceEventAt].filter(Boolean);
              const bids = numericValue(row.totalBids);
              return (
                <div
                  key={`${row.vendorId}-${row.projectName}-${index}`}
                  data-testid="gc-roi-project-approval-row"
                  style={{ ...S.queueRow, marginBottom: 0, padding: "0.7rem 0.8rem" }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={S.queueName}>{row.projectName || "Project name unavailable"}</p>
                    <p style={S.queueMeta}>
                      {row.tradePartner}
                      {row.tradeType ? ` · ${row.tradeType}` : ""}
                      {row.projectLocation ? ` · ${row.projectLocation}` : ""}
                    </p>
                    {row.liquidityRisk ? <span style={S.badge}>Liquidity risk</span> : null}
                    {row.renewalDue ? <span style={{ ...S.badge, color: "#f3c56b" }}>Renewal due</span> : null}
                    {row.approvedWithRmp ? <span style={{ ...S.badge, color: "#7eb6ff" }}>Approved W/RMP</span> : null}
                    {row.approvalResponse ? (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Approval response</span>
                        {row.approvalResponse}
                      </p>
                    ) : null}
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Approved with RMP</span>
                      {row.approvedWithRmp ? "Yes" : "No"}
                    </p>
                    {award && (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Historical award value</span>
                        {award}
                      </p>
                    )}
                    {bids != null && (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Bid activity</span>
                        {bids.toLocaleString()} matched bids
                      </p>
                    )}
                    {sourceBits.length > 0 && (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Source</span>
                        {sourceBits.join(" · ")}
                      </p>
                    )}
                    {classification ? (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Financial / Safety classification</span>
                        {classification}
                      </p>
                    ) : null}
                    {activity ? (
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Current activity</span>
                        {activity}
                      </p>
                    ) : null}
                    {requirements.length > 0 ? (
                      <details data-testid="gc-roi-rmp-requirements">
                        <summary style={{ ...S.drillHint, cursor: "pointer", marginTop: 8 }}>
                          {requirements.length} RMP requirement{requirements.length === 1 ? "" : "s"}
                        </summary>
                        <ul style={{ margin: "8px 0 0", paddingLeft: 18, color: "#d5dbe2", fontSize: 12 }}>
                          {requirements.map((text) => (
                            <li key={text}>{text}</li>
                          ))}
                        </ul>
                      </details>
                    ) : (
                      <p style={S.metricNote}>No RMP requirements on this approval.</p>
                    )}
                    {showRecommendedPath && (() => {
                      const path = recommendedPathForApproval(row);
                      return path ? (
                        <p style={S.queueField} data-testid="gc-roi-recommended-path">
                          <span style={S.queueLabel}>Recommended ConstructPay path</span>
                          {path.label}
                          <span style={{ display: "block", color: "#8d9aaa", fontWeight: 400 }}>{path.note}</span>
                        </p>
                      ) : null;
                    })()}
                    {onOpenVendor && row.vendorId ? (
                      <button
                        type="button"
                        style={{ ...S.secondaryBtn, marginTop: 8 }}
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenVendor(row, filter);
                        }}
                      >
                        {vendorActionLabel}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </>
      )}
    </article>
  );
}

function ProjectInlineDrilldown({
  projectId,
  projectName,
  projectRow,
  growthRow,
  approvalRows,
  onClose,
  onReviewEvidence,
}: {
  projectId?: string | null;
  projectName: string;
  projectRow?: ProjectConcentrationRow | null;
  growthRow?: GrowthConcentrationRow | null;
  approvalRows: ProjectApprovalRow[] | null;
  onClose: () => void;
  onReviewEvidence: (row: ProjectApprovalRow, reason: EvidenceFilter) => void;
}): React.ReactElement {
  const scopedRows = (approvalRows || []).filter((row) => rowMatchesProjectScope(row, projectId, projectId ? null : projectName));
  const attentionCount = countForFilter(scopedRows, "requires-attention");
  const liquidityCount = countForFilter(scopedRows, "liquidity-risk");
  return (
    <article data-testid="gc-roi-project-drilldown" style={{ ...S.card, marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={S.intelTitle}>Project drilldown</h3>
          <p data-testid="gc-roi-project-name" style={S.intelValue}>{projectName}</p>
          <p style={S.metricNote} data-testid="gc-roi-project-attention-summary">
            {attentionCount.toLocaleString()} trade partner{attentionCount === 1 ? "" : "s"} require attention
            {liquidityCount > 0 ? ` · ${liquidityCount.toLocaleString()} with source-backed liquidity risk` : ""}
          </p>
          {projectRow && (
            <div data-testid="gc-roi-project-context" style={{ ...S.metricGrid, marginTop: 10 }}>
              <div>
                <p style={S.queueLabel}>Historical known project value</p>
                <p style={S.intelValue}>{formatAward(projectRow.knownAwardValue) || "—"}</p>
                <p style={S.metricNote}>Historical award. Not current exposure.</p>
              </div>
              <div>
                <p style={S.queueLabel}>Approved W/RMP</p>
                <p style={S.intelValue}>{(projectRow.approvedWithRmpCount || 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={S.queueLabel}>Approved W/RMP + liquidity risk</p>
                <p style={{ ...S.intelValue, ...S.riskValue }}>
                  {(projectRow.approvedWithRmpLiquidityRiskCount || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p style={S.queueLabel}>Trade partners</p>
                <p style={S.intelValue}>{(projectRow.tradePartnerCount || 0).toLocaleString()}</p>
              </div>
            </div>
          )}
          {!projectRow && growthRow && (
            <p style={S.intelNote}>
              {(growthRow.bidderCount || 0).toLocaleString()} matched bidders · {(growthRow.bidCount || 0).toLocaleString()} bids. No TradeTapp approval rows share this exact project name.
            </p>
          )}
        </div>
        <button type="button" style={S.secondaryBtn} onClick={onClose}>Close</button>
      </div>
      <ProjectApprovalEvidenceList
        rows={approvalRows}
        loading={approvalRows == null}
        projectId={projectId}
        projectName={projectId ? null : projectName}
        defaultFilter={defaultPartnerFilter(projectRow)}
        emptyCopy="No project-approval evidence is attached to this project in the current source set."
        title="Trade-partner evidence"
        onOpenVendor={onReviewEvidence}
        vendorActionLabel="Review evidence"
        showRecommendedPath
      />
    </article>
  );
}

function ContextualTradePartnerDrawer({
  row,
  reason,
  onClose,
  onQueued,
}: {
  row: ProjectApprovalRow;
  reason: EvidenceFilter;
  onClose: () => void;
  onQueued: () => void;
}): React.ReactElement {
  const [adding, setAdding] = useState(false);
  const [queued, setQueued] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const path = recommendedPathForApproval(row);
  const award = formatAward(row.subcontractAwardValue);
  const bids = numericValue(row.totalBids);
  const requirements = row.rmpRequirements || [];
  const sourceBits = [row.sourceSystem, row.sourceEventAt].filter(Boolean);

  useEffect(() => {
    let cancelled = false;
    (gcRoiAPI as any)
      .getQueue()
      .then((res: any) => {
        if (cancelled) return;
        const ids = new Set((res.data || []).map((item: any) => String(item.vendor_id || "")));
        setQueued(Boolean(row.vendorId) && ids.has(String(row.vendorId)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [row.vendorId]);

  const addThisPartner = async () => {
    if (!row.vendorId || queued || adding) return;
    setAdding(true);
    setError(null);
    try {
      const res = await (gcRoiAPI as any).addToQueue({
        metricKey: queueMetricForEvidenceReason(reason),
        vendors: [{ vendorId: row.vendorId, companyName: row.tradePartner }],
      });
      const added = res.data?.added ?? 0;
      setQueued(true);
      setMessage(
        added > 0
          ? "1 trade partner added to Introduction Queue. No outreach was triggered."
          : "This trade partner is already in the queue."
      );
      onQueued();
    } catch (e: any) {
      setError(queueAddErrorMessage(e));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="gc-roi-modal-overlay" data-testid="gc-roi-contextual-evidence" role="dialog" aria-label={`Evidence for ${row.tradePartner}`}>
      <div className="gc-roi-modal-card">
        <div className="gc-roi-modal-header">
          <div>
            <p className="gc-roi-modal-title" data-testid="gc-roi-contextual-partner">{row.tradePartner}</p>
            <p className="gc-roi-modal-subtitle">Source-backed project relationship evidence. No outreach has occurred.</p>
          </div>
          <button type="button" className="gc-roi-modal-close" onClick={onClose} aria-label="Close">{"\u00D7"}</button>
        </div>
        <div className="gc-roi-modal-meta">
          <div>
            <p className="gc-roi-meta-label">Project</p>
            <p className="gc-roi-meta-value" data-testid="gc-roi-contextual-project">{row.projectName || "Project name unavailable"}</p>
          </div>
          <div>
            <p className="gc-roi-meta-label">Why this relationship is shown</p>
            <p className="gc-roi-meta-value" data-testid="gc-roi-contextual-reason">{evidenceReasonLabel(reason)}</p>
          </div>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {row.approvalResponse ? (
            <p style={S.queueField}><span style={S.queueLabel}>Approval response</span>{row.approvalResponse}</p>
          ) : null}
          <p style={S.queueField}><span style={S.queueLabel}>Approved W/RMP</span>{row.approvedWithRmp ? "Yes" : "No"}</p>
          {row.liquidityRisk === true ? (
            <p style={S.queueField}><span style={S.queueLabel}>Liquidity risk</span>Yes</p>
          ) : null}
          {row.renewalDue === true ? (
            <p style={S.queueField}><span style={S.queueLabel}>Renewal due</span>Yes</p>
          ) : null}
          {award ? (
            <p style={S.queueField}><span style={S.queueLabel}>Historical award value</span>{award}</p>
          ) : null}
          {bids != null ? (
            <p style={S.queueField}><span style={S.queueLabel}>Bid activity</span>{bids.toLocaleString()} matched bids</p>
          ) : null}
          {requirements.length > 0 ? (
            <div>
              <p style={S.queueLabel}>RMP requirements</p>
              <ul data-testid="gc-roi-contextual-rmp" style={{ margin: "4px 0 0", paddingLeft: 18, color: "#d5dbe2", fontSize: 13 }}>
                {requirements.map((text) => <li key={text}>{text}</li>)}
              </ul>
            </div>
          ) : null}
          {sourceBits.length > 0 ? (
            <p style={S.queueField}><span style={S.queueLabel}>Source</span>{sourceBits.join(" · ")}</p>
          ) : (
            <p style={S.queueField}><span style={S.queueLabel}>Source</span>TradeTapp closed approval</p>
          )}
          {path ? (
            <p style={S.queueField} data-testid="gc-roi-contextual-path">
              <span style={S.queueLabel}>Recommended ConstructPay path</span>
              {path.label}
              <span style={{ display: "block", color: "#8d9aaa", fontWeight: 400 }}>{path.note}</span>
            </p>
          ) : null}
        </div>
        {message ? <p style={{ ...S.metricNote, color: "#70e1b2", marginTop: 12 }}>{message}</p> : null}
        {error ? <p style={{ ...S.metricNote, color: "#ff8f8f", marginTop: 12 }}>{error}</p> : null}
        <div className="gc-roi-modal-actions">
          <button type="button" className="gc-roi-btn-secondary" onClick={onClose}>Close</button>
          {!queued && row.vendorId ? (
            <button
              type="button"
              className="gc-roi-btn-primary"
              disabled={adding}
              onClick={addThisPartner}
            >
              {adding ? "Adding..." : "Add this trade partner to Introduction Queue"}
            </button>
          ) : (
            <p className="gc-roi-meta-sub">Already in Introduction Queue. No outreach was triggered.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ClickableCard({
  children,
  onClick,
  style,
  testId,
}: {
  children?: React.ReactNode;
  onClick: () => void;
  style?: React.CSSProperties;
  testId?: string;
}): React.ReactElement {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      data-testid={testId}
      style={{
        ...(style || S.card),
        cursor: "pointer",
        borderColor: hovered ? "#7eb6ff" : "#25303d",
        transition: "border-color 0.15s",
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </div>
  );
}

export default function GcRoiDashboard() {
  const [data, setData] = useState<GcRoiSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<WorkspaceView>(() =>
    typeof window === "undefined" ? "overview" : viewFromHash(window.location.hash)
  );
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKey, setDrawerKey] = useState<string | null>(null);
  const [drawerLabel, setDrawerLabel] = useState("");
  const [queue, setQueue] = useState<QueueWorkflowItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueSearch, setQueueSearch] = useState("");
  const [actionItem, setActionItem] = useState<QueueWorkflowItem | null>(null);
  const [trackerItem, setTrackerItem] = useState<QueueWorkflowItem | null>(null);
  const [approvalRows, setApprovalRows] = useState<ProjectApprovalRow[] | null>(null);
  const [contextualRow, setContextualRow] = useState<ProjectApprovalRow | null>(null);
  const [contextualReason, setContextualReason] = useState<EvidenceFilter>("all");
  const [protectSearchQ, setProtectSearchQ] = useState("");
  const [protectMatches, setProtectMatches] = useState<TradePartnerMatch[]>([]);
  const [protectSearchError, setProtectSearchError] = useState<string | null>(null);
  const [protectSearching, setProtectSearching] = useState(false);
  const [quickAddQ, setQuickAddQ] = useState("");
  const [quickAddMatches, setQuickAddMatches] = useState<TradePartnerMatch[]>([]);
  const [quickAddSelected, setQuickAddSelected] = useState<TradePartnerMatch | null>(null);
  const [quickAddNotes, setQuickAddNotes] = useState("");
  const [quickAddError, setQuickAddError] = useState<string | null>(null);
  const [quickAddMessage, setQuickAddMessage] = useState<string | null>(null);
  const [quickAddSearching, setQuickAddSearching] = useState(false);
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [pendingFormOpen, setPendingFormOpen] = useState(false);
  const [pendingForm, setPendingForm] = useState({
    companyName: "",
    primaryContactName: "",
    primaryContactEmail: "",
    sourceEvidence: "",
    notes: "",
  });
  const [pendingError, setPendingError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingSubmitting, setPendingSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [notesDetailId, setNotesDetailId] = useState<string | null>(null);

  useEffect(() => {
    (gcRoiAPI as any)
      .getSummary()
      .then((res: any) => setData(res.data))
      .catch((err: any) => setError(err?.message || "Failed to load"));
  }, []);

  const loadQueue = useCallback(() => {
    setQueueLoading(true);
    (gcRoiAPI as any)
      .getQueue()
      .then((res: any) => setQueue(res.data || []))
      .catch(() => {})
      .finally(() => setQueueLoading(false));
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    if (activeView !== "protect" && activeView !== "portfolio-intelligence" && activeView !== "grow") return;
    if (approvalRows != null) return;
    let cancelled = false;
    (gcRoiAPI as any)
      .getProjectApprovals()
      .then((res: any) => {
        if (!cancelled) setApprovalRows(flattenProjectApprovals(res.data));
      })
      .catch(() => {
        if (!cancelled) setApprovalRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeView, approvalRows]);

  useEffect(() => {
    const onHashChange = () => setActiveView(viewFromHash(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const selectView = (id: WorkspaceView) => {
    setActiveView(id);
    if (typeof window !== "undefined" && window.location.hash !== `#${id}`) {
      window.history.replaceState(null, "", `#${id}`);
    }
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  };

  const openDrawer = (key: string, label: string) => {
    setContextualRow(null);
    setDrawerKey(key);
    setDrawerLabel(label);
    setDrawerOpen(true);
  };

  const openContextualEvidence = (row: ProjectApprovalRow, reason: EvidenceFilter) => {
    setDrawerOpen(false);
    setDrawerKey(null);
    setContextualReason(reason);
    setContextualRow(row);
  };

  const selectProject = (name: string, projectId?: string | null) => {
    const resolvedId = projectId ?? resolveProjectIdFromApprovals(name, approvalRows);
    const sameSelection = resolvedId
      ? selectedProjectId === resolvedId
      : selectedProjectName === name;
    if (sameSelection) {
      setSelectedProjectName(null);
      setSelectedProjectId(null);
      return;
    }
    setSelectedProjectName(name);
    setSelectedProjectId(resolvedId);
  };

  useEffect(() => {
    if ((!selectedProjectName && !selectedProjectId) || typeof document === "undefined") return;
    const blocks = Array.from(document.querySelectorAll('[data-testid="gc-roi-project-block"]'));
    const block = blocks.find((el) =>
      (selectedProjectId && el.getAttribute("data-project-id") === selectedProjectId)
      || (!selectedProjectId && el.getAttribute("data-project-name") === selectedProjectName),
    );
    if (block instanceof HTMLElement && typeof block.scrollIntoView === "function") {
      block.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedProjectName, selectedProjectId]);

  const handleRemoveFromQueue = async (id: string) => {
    try {
      await (gcRoiAPI as any).removeFromQueue(id);
      loadQueue();
    } catch {}
  };

  const runProtectSearch = async () => {
    const q = protectSearchQ.trim();
    setProtectSearchError(null);
    setProtectMatches([]);
    if (!q) {
      setProtectSearchError("Enter a trade partner name to search.");
      return;
    }
    setProtectSearching(true);
    try {
      const res = await (gcRoiAPI as any).searchTradePartners(q);
      setProtectMatches(res.data?.matches || []);
    } catch (e: any) {
      setProtectSearchError(e?.message ?? "Trade partner search failed");
    } finally {
      setProtectSearching(false);
    }
  };

  const runQuickAddSearch = async () => {
    const q = quickAddQ.trim();
    setQuickAddError(null);
    setQuickAddMessage(null);
    setQuickAddSelected(null);
    setQuickAddMatches([]);
    if (!q) {
      setQuickAddError("Enter a trade partner name to search.");
      return;
    }
    setQuickAddSearching(true);
    try {
      const res = await (gcRoiAPI as any).searchTradePartners(q);
      const matches: TradePartnerMatch[] = res.data?.matches || [];
      setQuickAddMatches(matches);
      if (matches.length === 0) {
        setQuickAddError("No exact trade partner match found. Select an existing trade partner — do not invent a vendor.");
      } else if (matches.length > 1) {
        setQuickAddError("Multiple matches found. Select one exact trade partner before adding.");
      }
    } catch (e: any) {
      setQuickAddError(e?.message ?? "Trade partner search failed");
    } finally {
      setQuickAddSearching(false);
    }
  };

  const handleQuickAdd = async () => {
    setQuickAddError(null);
    setQuickAddMessage(null);
    if (!quickAddSelected?.vendorId) {
      if (quickAddMatches.length === 0) {
        setQuickAddError("No exact trade partner match found. Select an existing trade partner — do not invent a vendor.");
      } else if (quickAddMatches.length > 1) {
        setQuickAddError("Multiple matches found. Select one exact trade partner before adding.");
      } else {
        setQuickAddError("Select an exact trade partner match before adding.");
      }
      return;
    }
    setQuickAddSubmitting(true);
    try {
      const res = await (gcRoiAPI as any).addToQueue({
        metricKey: "MANUAL_QUICK_ADD",
        manuallyAdded: true,
        notes: quickAddNotes.trim() || null,
        vendors: [{ vendorId: quickAddSelected.vendorId, companyName: quickAddSelected.companyName }],
      });
      const added = res.data?.added ?? 0;
      setQuickAddMessage(
        added > 0
          ? "1 trade partner added to Introduction Queue. No outreach was triggered."
          : "This trade partner is already in the queue."
      );
      setQuickAddQ("");
      setQuickAddNotes("");
      setQuickAddSelected(null);
      setQuickAddMatches([]);
      loadQueue();
    } catch (e: any) {
      setQuickAddError(queueAddErrorMessage(e));
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  const handleAddSourcePending = async () => {
    setPendingError(null);
    setPendingMessage(null);
    if (!pendingForm.companyName.trim()) {
      setPendingError("Enter the trade partner's company name exactly as shown in the source.");
      return;
    }
    if (!pendingForm.sourceEvidence.trim()) {
      setPendingError("Describe the source evidence (for example, the TradeTapp invitation).");
      return;
    }
    setPendingSubmitting(true);
    try {
      const res = await (gcRoiAPI as any).addSourcePendingToQueue({
        companyName: pendingForm.companyName.trim(),
        primaryContactName: pendingForm.primaryContactName.trim() || null,
        primaryContactEmail: pendingForm.primaryContactEmail.trim() || null,
        sourceEvidence: pendingForm.sourceEvidence.trim(),
        notes: pendingForm.notes.trim() || null,
      });
      setPendingMessage(
        res.data?.alreadyQueued
          ? "This source-pending trade partner is already in the queue."
          : "Source-pending trade partner added to Introduction Queue. No outreach was triggered."
      );
      setPendingForm({ companyName: "", primaryContactName: "", primaryContactEmail: "", sourceEvidence: "", notes: "" });
      loadQueue();
    } catch (e: any) {
      setPendingError(e?.response?.data?.message ?? e?.message ?? "Failed to add to queue");
    } finally {
      setPendingSubmitting(false);
    }
  };

  const startEditNote = (item: QueueWorkflowItem) => {
    setEditingNoteId(item.id);
    setNoteDraft(item.notes || "");
  };

  const saveQueueNote = async (item: QueueWorkflowItem) => {
    if (noteSaving) return;
    setNoteSaving(true);
    try {
      await (gcRoiAPI as any).updateQueueItem(item.id, { notes: noteDraft });
      setEditingNoteId(null);
      loadQueue();
    } catch {
      /* keep editor open on failure */
    } finally {
      setNoteSaving(false);
    }
  };

  const handlePrimaryAdvance = async (item: QueueWorkflowItem) => {
    const next = item.permitted_next_status;
    if (!next) return;
    await (gcRoiAPI as any).updateQueueItem(item.id, { status: next, notes: item.notes });
    setActionItem(null);
    loadQueue();
  };

  const openActionModal = (item: QueueWorkflowItem) => {
    setActionItem(item);
  };

  const openTracker = (item: QueueWorkflowItem) => {
    setTrackerItem(item);
  };

  if (error) {
    return (
      <div style={S.shell}>
        <p style={{ color: "#ff8f8f", padding: "2rem" }}>Error: {error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={S.shell}>
        <p style={{ color: "#8d9aaa", padding: "2rem" }}>Loading...</p>
      </div>
    );
  }

  const projectsPending = sourcePendingCount(data.totalProjects, data.totalProjectsStatus);
  const opportunitiesPending = sourcePendingCount(data.totalOpportunities, data.totalOpportunitiesStatus);
  const projectConcentration = data.portfolio?.projectConcentration || [];
  const growthConcentration = data.portfolio?.growthConcentration || [];
  const projectConcentrationPending =
    data.portfolio?.projectConcentrationStatus === "SOURCE_PENDING" || projectConcentration.length === 0;
  const growthPending =
    data.portfolio?.growthConcentrationStatus === "SOURCE_PENDING" || growthConcentration.length === 0;
  const readiness = data.readiness;
  const financialRmp = data.financialRmp;
  const queueFilter = queueSearch.trim().toLowerCase();
  const filteredQueue = queueFilter
    ? queue.filter((item) => String(item.company_name || "").toLowerCase().includes(queueFilter))
    : queue;
  const selectedProjectRow = projectConcentration.find((row) =>
    (selectedProjectId && row.matchedProjectId === selectedProjectId)
    || (!selectedProjectId && row.projectName === selectedProjectName),
  ) || null;
  const selectedGrowthRow = growthConcentration.find((row) =>
    (selectedProjectId && row.projectId === selectedProjectId)
    || (!selectedProjectId && row.projectName === selectedProjectName),
  ) || null;
  const projectAwardMax = Math.max(
    0,
    ...projectConcentration.map((row) => numericValue(row.knownAwardValue)).filter((value): value is number => value != null && value > 0)
  );
  const growthBidderMax = Math.max(
    0,
    ...growthConcentration.map((row) => Number(row.bidderCount || 0)).filter((value) => value > 0)
  );
  const openVendorFromEvidence = (row: ProjectApprovalRow) => {
    if (row.liquidityRisk) {
      openDrawer("AT_RISK_VENDORS", "At-Risk Vendors");
      return;
    }
    openDrawer("ACTIVE_MATCHED_BIDDERS", "Active Matched Bidders");
  };
  const historicalApprovedValue = formatAward(financialRmp?.historicalRmpAwardValue);
  const activityConfirmedCount = Number(financialRmp?.resolvedBiddingRmpCount || 0);
  const rmpDefaultFilter: EvidenceFilter = "requires-attention";
  const joinedSignals = [
    {
      label: "Liquidity-Risk Active Bidders",
      value: data.cards.liquidityRiskActiveBidders,
      note: `${formatFixed(data.cards.liquidityRiskActiveBidderPct, 1)}% of active bidders`,
      metricKey: "LIQUIDITY_RISK_ACTIVE_BIDDERS",
    },
    {
      label: "Renewal-Due Active Bidders",
      value: data.cards.renewalDueActiveBidders,
      note: `${formatFixed(data.cards.renewalDueActiveBidderPct, 1)}% of active bidders`,
      metricKey: "RENEWAL_DUE_ACTIVE_BIDDERS",
    },
    {
      label: "Surety-Backed Active Bidders",
      value: data.cards.suretyBackedActiveBidders,
      note: `${formatFixed(data.cards.suretyBackedActiveBidderPct, 1)}% of active bidders`,
      metricKey: "SURETY_BACKED_ACTIVE_BIDDERS",
    },
    {
      label: "Renewal + Liquidity Active Bidders",
      value: data.cards.renewalAndLiquidityActiveBidders,
      note: `${formatFixed(data.cards.renewalAndLiquidityActiveBidderPct, 1)}% of active bidders`,
      metricKey: "RENEWAL_LIQUIDITY_ACTIVE_BIDDERS",
    },
  ];

  return (
    <div style={S.shell}>
      <aside style={S.sidebar}>
        <div style={S.brand}>
          <span style={S.brandMark}>C</span> ConstructPay
        </div>
        <nav aria-label="GC Commercial Insights" style={S.nav}>
          {WORKSPACE_VIEWS.map((view) => {
            const selected = activeView === view.id;
            return (
              <button
                key={view.id}
                type="button"
                aria-current={selected ? "page" : undefined}
                onClick={() => selectView(view.id)}
                style={{ ...S.navLink, ...(selected ? S.navLinkActive : null) }}
              >
                {view.label}
              </button>
            );
          })}
        </nav>
      </aside>

      <main style={S.main} data-testid="gc-roi-active-view" data-active-view={activeView}>
        <header style={S.topbar}>
          <div>
            <div style={S.eyebrow}>Executive workspace</div>
            <h1 style={S.title}>GC Commercial Insights</h1>
            <p style={S.subtitle}>Organization-scoped portfolio signals and governed introductions.</p>
          </div>
          <button
            onClick={() => auth.signOut()}
            style={S.logout}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#3a4b5e")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#25303d")}
          >
            Logout
          </button>
        </header>

        {activeView === "overview" && (
        <section id="overview" data-testid="gc-roi-overview" aria-labelledby="overview-heading" style={S.section}>
          <h2 id="overview-heading" style={S.sectionHead}>Executive Action Center</h2>
          <p style={S.sectionCopy}>
            See who may need help, why the evidence supports review, and the smallest responsible next action.
            Introduction remains an explicit second step.
          </p>
          <h3 style={S.intelTitle}>Where attention may create value</h3>
          {(data.actionCenter || []).length === 0 ? (
            <p data-testid="gc-roi-action-center-empty" style={S.emptyQ}>
              No source-backed action opportunities are present in the current snapshot.
            </p>
          ) : (
            <div data-testid="gc-roi-action-center" style={S.actionGrid}>
              {(data.actionCenter || []).map((card) => (
                <article
                  key={card.id}
                  data-testid="gc-roi-action-card"
                  data-action-id={card.id}
                  style={S.card}
                >
                  <p style={S.intelTitle}>{card.family}</p>
                  <p style={S.intelValue}>{card.title}</p>
                  <p style={S.intelNote}>{card.signal}</p>
                  <p style={{ ...S.metricValue, ...S.positiveValue }}>{card.count.toLocaleString()}</p>
                  <p style={S.metricNote}>trade partners</p>
                  {card.context ? <p style={S.intelNote}>{card.context}</p> : null}
                  <p style={S.queueField}>
                    <span style={S.queueLabel}>Why this matters</span>
                    {card.whyThisMatters || card.signal}
                  </p>
                  <p style={S.queueField}>
                    <span style={S.queueLabel}>Why this is shown</span>
                    {card.whyShown}
                  </p>
                  <button
                    type="button"
                    style={{ ...S.primaryBtn, marginTop: 10 }}
                    onClick={() => openDrawer(card.metricKey, card.title)}
                  >
                    {card.cta}
                  </button>
                </article>
              ))}
            </div>
          )}
          <h3 style={S.intelTitle}>Portfolio snapshot</h3>
          <div data-testid="gc-roi-overview-grid" style={S.metricGrid}>
            <article style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Matched / prequalified</p>
              <p style={{ ...S.metricValue, ...S.positiveValue }}>
                {data.matchedTotal.toLocaleString()} / {data.prequalifiedTotal.toLocaleString()}
              </p>
              <p style={S.metricNote}>Organization-scoped</p>
            </article>
            <ClickableCard onClick={() => openDrawer("ACTIVE_MATCHED_BIDDERS", "Active Matched Bidders")} style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Matched trade partners</p>
              <p style={{ ...S.metricValue, ...S.positiveValue }}>{data.matchedTotal.toLocaleString()}</p>
              <p style={S.metricNote}>Active matched bidders</p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <article style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Matched bids</p>
              <p style={S.metricValue}>{data.totalMatchedBids.toLocaleString()}</p>
              <p style={S.metricNote}>Committed source total</p>
            </article>
            {!projectsPending && (
            <article style={{ ...S.card, ...S.metric }} data-testid="gc-roi-overview-projects">
              <p style={S.metricLabel}>Projects</p>
              <p style={S.metricValue}>{data.totalProjects!.toLocaleString()}</p>
              <p style={S.metricNote}>Distinct BuildingConnected projects with org-company bids</p>
            </article>
            )}
            {!opportunitiesPending && data.totalOpportunities != null && (
            <article style={{ ...S.card, ...S.metric }} data-testid="gc-roi-overview-opportunities">
              <p style={S.metricLabel}>Opportunities</p>
              <p style={S.metricValue}>{data.totalOpportunities.toLocaleString()}</p>
              <p style={S.metricNote}>Organization-scoped opportunities</p>
            </article>
            )}
            <article style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Bid Participation Rate</p>
              <p style={{ ...S.metricValue, ...S.positiveValue }}>{data.metrics.bidParticipationRate}%</p>
              <p style={S.metricNote}>of prequalified vendors submitted bids</p>
            </article>
            <article style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Bid Density</p>
              <p style={S.metricValue}>{String(data.metrics.bidDensity)}</p>
              <p style={S.metricNote}>avg bids per active vendor</p>
            </article>
            <article style={{ ...S.card, ...S.metric }}>
              <p style={S.metricLabel}>Avg Bid Value Coverage</p>
              <p style={S.metricValue}>{data.metrics.avgBidValueCoverage}%</p>
              <p style={S.metricNote}>bids with a submitted amount</p>
            </article>
            {joinedSignals.map((signal) => (
              <div key={signal.metricKey} style={{ minWidth: 0 }}>
              <ClickableCard
                onClick={() => openDrawer(signal.metricKey, signal.label)}
                style={{ ...S.card, ...S.metric }}
              >
                <p style={S.metricLabel}>{signal.label}</p>
                <p style={{ ...S.metricValue, ...S.riskValue }}>{signal.value.toLocaleString()}</p>
                <p style={S.metricNote}>{signal.note}</p>
                <p style={S.drillHint}>↗ View vendors</p>
              </ClickableCard>
              </div>
            ))}
          </div>
          <div style={{ ...S.twoCol, marginTop: 10 }}>
            <button type="button" onClick={() => selectView("grow")} style={{ ...S.card, color: "inherit", cursor: "pointer", textAlign: "left", width: "100%" }}>
              <p style={S.intelTitle}>Grow pathway</p>
              <p style={S.intelValue}>Signal: bid participation {formatFixed(data.metrics.bidParticipationRate, 1)}%</p>
              <p style={S.intelNote}>Implication: source-backed coverage of prequalified vendors who bid.</p>
              <p style={S.pathwayAction}>Next action: Open Grow</p>
            </button>
            <button type="button" onClick={() => selectView("protect")} style={{ ...S.card, color: "inherit", cursor: "pointer", textAlign: "left", width: "100%" }}>
              <p style={S.intelTitle}>Protect pathway</p>
              <p style={S.intelValue}>
                Signal: {data.cards.liquidityRiskActiveBidders.toLocaleString()} liquidity-risk active bidders
              </p>
              <p style={S.intelNote}>Implication: joined liquidity and renewal evidence is available for review.</p>
              <p style={S.pathwayAction}>Next action: Open Protect</p>
            </button>
          </div>
        </section>
        )}

        {activeView === "portfolio-intelligence" && (
        <section
          id="portfolio-intelligence"
          data-testid="gc-roi-portfolio-intelligence"
          aria-labelledby="portfolio-heading"
          style={S.section}
        >
          <h2 id="portfolio-heading" style={S.sectionHead}>Portfolio Intelligence</h2>
          <p style={S.sectionCopy}>
            Ranked historical award concentration by project. Click a project to inspect trade-partner evidence.
          </p>
          <article style={{ ...S.card, ...S.intelCard, marginTop: 10 }} data-testid="gc-roi-concentration-panel">
            <h3 style={S.intelTitle}>Project concentration</h3>
            {projectConcentrationPending ? (
              <p style={S.emptyQ}>No organization-scoped project-level concentration is available in the current source set.</p>
            ) : (
              <div data-testid="gc-roi-concentration-rows" style={{ display: "grid", gap: 8 }}>
                {projectConcentration.map((row) => {
                  const award = numericValue(row.knownAwardValue);
                  const title = String(row.projectName || "Unnamed project");
                  const rowProjectId = row.matchedProjectId
                    ?? resolveProjectIdFromApprovals(title, approvalRows);
                  const selected = selectedProjectId
                    ? rowProjectId === selectedProjectId
                    : selectedProjectName === row.projectName;
                  return (
                    <div
                      key={rowProjectId || title}
                      data-testid="gc-roi-project-block"
                      data-project-name={title}
                      data-project-id={rowProjectId || undefined}
                    >
                    <RankedBarRow
                      testId="gc-roi-concentration-row"
                      projectName={title}
                      title={title}
                      valueLabel={formatAward(award) || "—"}
                      ratio={award != null && projectAwardMax > 0 ? award / projectAwardMax : 0}
                      selected={selected}
                      onClick={() => selectProject(title, rowProjectId)}
                      actionLabel="Review trade partners →"
                      meta={`${(row.approvedWithRmpCount || 0).toLocaleString()} Approved W/RMP · ${(row.approvedWithRmpLiquidityRiskCount || 0).toLocaleString()} Approved W/RMP + liquidity risk · ${(row.tradePartnerCount || 0).toLocaleString()} trade partners`}
                    />
                    {selected && (
                      <ProjectInlineDrilldown
                        projectId={rowProjectId}
                        projectName={title}
                        projectRow={row}
                        approvalRows={approvalRows}
                        onClose={() => { setSelectedProjectName(null); setSelectedProjectId(null); }}
                        onReviewEvidence={openContextualEvidence}
                      />
                    )}
                    </div>
                  );
                })}
              </div>
            )}
            {selectedProjectName && !projectConcentration.some((row) =>
              (selectedProjectId && (row.matchedProjectId === selectedProjectId
                || resolveProjectIdFromApprovals(String(row.projectName || ""), approvalRows) === selectedProjectId))
              || (!selectedProjectId && row.projectName === selectedProjectName),
            ) && (
              <div data-testid="gc-roi-project-block" data-project-name={selectedProjectName} data-project-id={selectedProjectId || undefined} style={{ marginTop: 8 }}>
                <ProjectInlineDrilldown
                  projectId={selectedProjectId}
                  projectName={selectedProjectName}
                  projectRow={selectedProjectRow}
                  growthRow={selectedGrowthRow}
                  approvalRows={approvalRows}
                  onClose={() => { setSelectedProjectName(null); setSelectedProjectId(null); }}
                  onReviewEvidence={openContextualEvidence}
                />
              </div>
            )}
            <p style={S.intelNote}>Historical known award. Not current active exposure.</p>
          </article>
        </section>
        )}

        {activeView === "grow" && (
        <section id="grow" data-testid="gc-roi-grow" aria-labelledby="grow-heading" style={S.section}>
          <h2 id="grow-heading" style={S.sectionHead}>Grow</h2>
          <p style={S.sectionCopy}>Source-backed capacity and bid concentration. No inferred financial readiness score.</p>
          <div style={S.metricGrid}>
            <article style={{ ...S.card, ...S.intelCard }}>
              <h3 style={S.intelTitle}>Bid participation</h3>
              <p style={S.intelValue}>{formatFixed(data.metrics.bidParticipationRate, 1)}%</p>
              <p style={S.intelNote}>Matched trade-partner participation</p>
            </article>
            <article style={{ ...S.card, ...S.intelCard }}>
              <h3 style={S.intelTitle}>Bid density</h3>
              <p style={S.intelValue}>{String(data.metrics.bidDensity)}</p>
              <p style={S.intelNote}>Average bids per active vendor</p>
            </article>
            <article style={{ ...S.card, ...S.intelCard }}>
              <h3 style={S.intelTitle}>Average bid value coverage</h3>
              <p style={S.intelValue}>{data.metrics.avgBidValueCoverage}%</p>
              <p style={S.intelNote}>Current source coverage</p>
            </article>
          </div>
          {readiness?.classifierAvailable ? (
            <article style={{ ...S.card, ...S.intelCard, marginTop: 10 }} data-testid="gc-roi-grow-readiness">
              <h3 style={S.intelTitle}>Readiness</h3>
              <p style={S.intelValue}>{(readiness.verifiedReady ?? 0).toLocaleString()} Verified Ready</p>
              <p style={S.metricNote}>{(readiness.reviewRecommended ?? 0).toLocaleString()} Review Recommended</p>
            </article>
          ) : (
            <div style={{ ...S.metricGrid, marginTop: 10 }} data-testid="gc-roi-grow-financial-evidence">
              <ClickableCard
                testId="gc-roi-grow-current-financial-evidence"
                onClick={() => openDrawer("CURRENT_FINANCIAL_EVIDENCE", "Current Financial Evidence")}
                style={S.card}
              >
                <p style={S.metricLabel}>Companies with current financial evidence</p>
                <p style={S.metricValue}>{(readiness?.withFinancialEvidence ?? 0).toLocaleString()}</p>
                <p style={S.metricNote}>Working capital and net cash flow on file</p>
                <p style={S.drillHint}>↗ Review capital readiness</p>
              </ClickableCard>
              <ClickableCard
                testId="gc-roi-grow-negative-working-capital"
                onClick={() => openDrawer("NEGATIVE_WORKING_CAPITAL", "Negative Working Capital")}
                style={S.card}
              >
                <p style={S.metricLabel}>Companies with negative working capital</p>
                <p style={{ ...S.metricValue, ...S.riskValue }}>{(readiness?.negativeWorkingCapital ?? 0).toLocaleString()}</p>
                <p style={S.metricNote}>Source-backed working capital below zero. Not a Not Ready classification.</p>
                <p style={S.drillHint}>↗ Review capital readiness</p>
              </ClickableCard>
              <ClickableCard onClick={() => openDrawer("AT_RISK_VENDORS", "At-Risk Vendors")} style={S.card}>
                <p style={S.metricLabel}>Companies with liquidity-risk indicators</p>
                <p style={{ ...S.metricValue, ...S.riskValue }}>{(readiness?.liquidityRisk ?? data.cards.atRiskVendors).toLocaleString()}</p>
                <p style={S.metricNote}>Existing liquidity-risk flag. Not a readiness classifier.</p>
                <p style={S.drillHint}>↗ View vendors</p>
              </ClickableCard>
              <ClickableCard
                testId="gc-roi-grow-surety-on-file"
                onClick={() => openDrawer("SURETY_CAPACITY_ON_FILE", "Surety Capacity On File")}
                style={S.card}
              >
                <p style={S.metricLabel}>Companies with surety capacity on file</p>
                <p style={S.metricValue}>{(readiness?.suretyCapacityOnFile ?? 0).toLocaleString()}</p>
                <p style={S.metricNote}>Surety context only. Not readiness proof.</p>
                <p style={S.drillHint}>↗ View vendors</p>
              </ClickableCard>
            </div>
          )}
          <article data-testid="gc-roi-bonding-opportunities" style={{ ...S.card, marginTop: 10 }}>
            <h3 style={S.intelTitle}>Bonding & Growth Opportunities</h3>
            <p style={S.intelNote}>
              Aggregate and single-project surety capacity are context only. Aggregate surety capacity alone is not a BondReady need.
            </p>
            <div style={{ ...S.twoCol, marginTop: 10 }}>
              <div>
                <p style={S.metricLabel}>Aggregate surety capacity</p>
                <p style={S.intelValue}>{formatBillions(data.cards.totalSuretyAggregateCapacity)}</p>
              </div>
              <div>
                <p style={S.metricLabel}>Single-project surety capacity</p>
                <p style={S.intelValue}>{formatBillions(data.cards.totalSuretySingleCapacity)}</p>
              </div>
            </div>
            <button
              type="button"
              data-testid="gc-roi-bondready-cta"
              style={{ ...S.primaryBtn, marginTop: 12 }}
              onClick={() => openDrawer("BONDREADY_REVIEW_CANDIDATES", "BondReady Review Candidates")}
            >
              Review BondReady opportunities
            </button>
          </article>
          <article style={{ ...S.card, marginTop: 10 }} data-testid="gc-roi-growth-concentration">
            <h3 style={S.intelTitle}>Growth concentration</h3>
            {growthPending ? (
              <p style={S.emptyQ}>No organization-scoped bid concentration is available in the current source set.</p>
            ) : (
              <div data-testid="gc-roi-growth-concentration-rows" style={{ display: "grid", gap: 8 }}>
                {growthConcentration.map((row) => {
                  const bidders = Number(row.bidderCount || 0);
                  const overlay = projectConcentration.find((item) => item.projectName && item.projectName === row.projectName);
                  const title = String(row.projectName || "Unnamed project");
                  const overlayMeta = overlay
                    ? ` · ${(overlay.approvedWithRmpCount || 0).toLocaleString()} Approved W/RMP · ${(overlay.approvedWithRmpLiquidityRiskCount || 0).toLocaleString()} Approved W/RMP + liquidity risk`
                    : "";
                  return (
                    <div key={String(row.projectId || row.projectName)}>
                    <RankedBarRow
                      testId="gc-roi-growth-bar"
                      title={title}
                      valueLabel={`${bidders.toLocaleString()} bidders`}
                      ratio={bidders > 0 && growthBidderMax > 0 ? bidders / growthBidderMax : 0}
                      selected={selectedProjectId
                        ? row.projectId === selectedProjectId
                        : selectedProjectName === row.projectName}
                      onClick={() => {
                        selectProject(String(row.projectName || ""), row.projectId || null);
                        selectView("portfolio-intelligence");
                      }}
                      meta={`${(row.bidCount || 0).toLocaleString()} bids${overlayMeta}`}
                    />
                    </div>
                  );
                })}
              </div>
            )}
            <p style={S.intelNote}>Current BuildingConnected bid activity by project. Click through for project evidence.</p>
          </article>
        </section>
        )}

        {activeView === "protect" && (
        <section id="protect" data-testid="gc-roi-protect" aria-labelledby="protect-heading" style={S.section}>
          <h2 id="protect-heading" style={S.sectionHead}>Protect</h2>
          <p style={S.sectionCopy}>Distinct joined-ecosystem measures and historical approval evidence.</p>
          <div style={{ marginBottom: "0.85rem" }} data-testid="gc-roi-protect-tp-search">
            <label style={S.fieldLabel} htmlFor="gc-roi-protect-search">
              Search trade partners
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <input
                id="gc-roi-protect-search"
                aria-label="Search trade partners"
                type="search"
                value={protectSearchQ}
                onChange={(e) => setProtectSearchQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runProtectSearch();
                  }
                }}
                style={{ ...S.searchInput, flex: "1 1 14rem" }}
                placeholder="Search by company name"
              />
              <button type="button" style={S.secondaryBtn} onClick={runProtectSearch} disabled={protectSearching}>
                {protectSearching ? "Searching..." : "Search"}
              </button>
            </div>
            {protectSearchError && <p style={S.errorText}>{protectSearchError}</p>}
            {protectMatches.length > 0 && (
              <ul style={{ listStyle: "none", margin: "0.55rem 0 0", padding: 0 }} data-testid="gc-roi-protect-tp-results">
                {protectMatches.map((match) => (
                  <li key={match.vendorId} style={{ ...S.queueRow, marginBottom: "0.4rem", padding: "0.65rem 0.8rem" }}>
                    <div>
                      <p style={S.queueName}>{match.companyName}</p>
                      <p style={S.queueMeta}>
                        {(match.trancheLabels || []).length > 0
                          ? (match.trancheLabels || []).join(" · ")
                          : "No tranche labels"}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div style={S.metricGrid}>
            <ClickableCard
              onClick={() => openDrawer("AT_RISK_VENDORS", "At-Risk Vendors")}
              style={{ ...S.card, ...S.metric }}
            >
              <div data-testid="gc-roi-at-risk-vendors">
                <p style={S.metricLabel}>At-Risk Vendors</p>
                <p style={{ ...S.metricValue, ...S.riskValue }}>
                  {data.cards.atRiskVendors.toLocaleString()}
                </p>
                <p style={S.metricNote}>
                  Source-provided liquidity-risk flag on prequalified vendors. Distinct from liquidity-risk active bidders.
                </p>
                <p style={S.drillHint}>↗ View vendors</p>
              </div>
            </ClickableCard>
            <ClickableCard onClick={() => openDrawer("LIQUIDITY_RISK_ACTIVE_BIDDERS", "Liquidity-Risk Active Bidders")}>
              <p style={S.metricLabel}>Liquidity-Risk Active Bidders</p>
              <p style={{ ...S.metricValue, ...S.riskValue }}>
                {data.cards.liquidityRiskActiveBidders.toLocaleString()}
              </p>
              <p style={S.metricNote}>
                {formatFixed(data.cards.liquidityRiskActiveBidderPct, 1)}% of active bidders
              </p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <ClickableCard onClick={() => openDrawer("RENEWAL_DUE_VENDORS", "Renewal-Due Vendors")}>
              <p style={S.metricLabel}>Renewal-Due Vendors</p>
              <p style={S.metricValue}>{data.cards.renewalDueVendors.toLocaleString()}</p>
              <p style={S.metricNote}>Vendors due for prequalification renewal</p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <ClickableCard onClick={() => openDrawer("RENEWAL_DUE_ACTIVE_BIDDERS", "Renewal-Due Active Bidders")}>
              <p style={S.metricLabel}>Renewal-Due Active Bidders</p>
              <p style={S.metricValue}>{data.cards.renewalDueActiveBidders.toLocaleString()}</p>
              <p style={S.metricNote}>
                {formatFixed(data.cards.renewalDueActiveBidderPct, 1)}% of active bidders
              </p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
          </div>
          <div style={{ ...S.metricGrid, marginTop: 10 }}>
            <ClickableCard onClick={() => openDrawer("SURETY_BACKED_ACTIVE_BIDDERS", "Surety-Backed Active Bidders")}>
              <p style={S.metricLabel}>Surety-Backed Active Bidders</p>
              <p style={S.metricValue}>{data.cards.suretyBackedActiveBidders.toLocaleString()}</p>
              <p style={S.metricNote}>
                {formatFixed(data.cards.suretyBackedActiveBidderPct, 1)}% of active bidders
              </p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <ClickableCard onClick={() => openDrawer("RENEWAL_LIQUIDITY_ACTIVE_BIDDERS", "Renewal + Liquidity Active Bidders")}>
              <p style={S.metricLabel}>Renewal + Liquidity Active Bidders</p>
              <p style={S.metricValue}>{data.cards.renewalAndLiquidityActiveBidders.toLocaleString()}</p>
              <p style={S.metricNote}>
                {formatFixed(data.cards.renewalAndLiquidityActiveBidderPct, 1)}% of active bidders
              </p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <ClickableCard
              testId="gc-roi-protect-aggregate-surety"
              onClick={() => openDrawer("SURETY_AGGREGATE_CAPACITY", "Aggregate Surety Capacity")}
            >
              <p style={S.metricLabel}>Aggregate Surety Capacity</p>
              <p style={S.metricValue}>{formatBillions(data.cards.totalSuretyAggregateCapacity)}</p>
              <p style={S.metricNote}>Surety aggregate capacity on file. Context only — not a BondReady decision.</p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
            <ClickableCard
              testId="gc-roi-protect-single-surety"
              onClick={() => openDrawer("SURETY_SINGLE_CAPACITY", "Single-Project Surety Capacity")}
            >
              <p style={S.metricLabel}>Single-Project Surety Capacity</p>
              <p style={S.metricValue}>{formatBillions(data.cards.totalSuretySingleCapacity)}</p>
              <p style={S.metricNote}>Single-project surety capacity on file</p>
              <p style={S.drillHint}>↗ View vendors</p>
            </ClickableCard>
          </div>
          <article data-testid="gc-roi-financial-rmp-evidence" style={{ ...S.card, marginTop: 10 }}>
            <h3 style={S.intelTitle}>Financial RMP / approval evidence</h3>
            <div style={S.metricGrid}>
              <div>
                <p style={S.metricLabel}>Matched project approvals</p>
                <p style={S.intelValue}>{(financialRmp?.projectApprovalCount ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={S.metricLabel}>Approved W/RMP</p>
                <p style={S.intelValue}>{(financialRmp?.approvedWithRmpCount ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={S.metricLabel}>Approved W/RMP + liquidity risk</p>
                <p style={{ ...S.intelValue, ...S.riskValue }}>{(financialRmp?.approvedWithRmpLiquidityRiskCount ?? 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={S.metricLabel}>Historical Approved Project Value</p>
                <p style={S.intelValue}>{historicalApprovedValue || "—"}</p>
                <p style={S.metricNote}>Historical award. Not current exposure.</p>
              </div>
            </div>
            <p style={S.intelNote}>
              {(financialRmp?.approvedWithRmpProjectCount ?? 0).toLocaleString()} distinct projects with Approved W/RMP · {(financialRmp?.approvedWithRmpTradePartnerCount ?? 0).toLocaleString()} distinct trade partners with Approved W/RMP
              {Number(financialRmp?.approvedWithRmpLiquidityRiskTradePartners || 0) > 0
                ? ` · ${(financialRmp?.approvedWithRmpLiquidityRiskTradePartners || 0).toLocaleString()} Approved W/RMP + liquidity-risk trade partners`
                : ""}
            </p>
            {activityConfirmedCount > 0 && (
              <p style={S.intelNote}>
                Current activity confirmed for {activityConfirmedCount.toLocaleString()} of {(financialRmp?.approvedWithRmpCount ?? 0).toLocaleString()} Approved W/RMP records
              </p>
            )}
            {financialRmp?.currentActiveExposureStatus === "SOURCE_PROVIDED" && financialRmp.resolvedActiveExposure != null && (
              <p style={S.intelNote}>
                Active financial RMP exposure {formatAward(financialRmp.resolvedActiveExposure)} from confirmed current bid activity only.
              </p>
            )}
          </article>
          <ProjectApprovalEvidenceList
            rows={approvalRows}
            loading={approvalRows == null}
            includeRenewalFilter
            includeProjectFilter
            defaultFilter={rmpDefaultFilter}
            emptyCopy="No project-approval evidence is attached to matched trade partners in the current source set."
            title="Approval evidence records"
            onOpenVendor={openVendorFromEvidence}
          />
        </section>
        )}

        {activeView === "introduction-queue" && (
        <section id="introduction-queue" data-testid="gc-roi-introduction-queue" aria-labelledby="queue-heading" style={S.section}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <h2 id="queue-heading" style={{ ...S.sectionHead, margin: 0 }}>Introduction Queue</h2>
            {queue.length > 0 && (
              <span style={S.statusBadge}>{queue.length}</span>
            )}
          </div>
          <p style={S.sectionCopy}>
            Introduction candidates selected for internal GC review. Adding a trade partner to the queue does not trigger outreach, notification, or underwriting. Directional use only — not an underwriting or exclusion decision tool.
          </p>

          <div style={{ marginBottom: "0.85rem" }}>
            <label style={S.fieldLabel} htmlFor="gc-roi-queue-filter">
              Filter queue
            </label>
            <input
              id="gc-roi-queue-filter"
              aria-label="Filter introduction queue"
              type="search"
              value={queueSearch}
              onChange={(e) => setQueueSearch(e.target.value)}
              style={S.searchInput}
              placeholder="Filter by company name"
            />
          </div>

          <div style={S.quickAddPanel} data-testid="gc-roi-quick-add">
            <p style={{ ...S.intelTitle, marginBottom: "0.35rem" }}>Quick Add</p>
            <p style={{ ...S.sectionCopy, marginBottom: "0.65rem" }}>
              Search an existing trade partner, select an exact match, optionally add a note, then queue. Fail closed if the match is missing or ambiguous.
            </p>
            <label style={S.fieldLabel} htmlFor="gc-roi-quick-add-search">
              Search existing trade partner
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.55rem" }}>
              <input
                id="gc-roi-quick-add-search"
                aria-label="Quick add trade partner search"
                type="search"
                value={quickAddQ}
                onChange={(e) => {
                  setQuickAddQ(e.target.value);
                  setQuickAddSelected(null);
                  setQuickAddError(null);
                  setQuickAddMessage(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    runQuickAddSearch();
                  }
                }}
                style={{ ...S.searchInput, flex: "1 1 14rem" }}
                placeholder="Company name"
              />
              <button type="button" style={S.secondaryBtn} onClick={runQuickAddSearch} disabled={quickAddSearching}>
                {quickAddSearching ? "Searching..." : "Search"}
              </button>
            </div>
            {quickAddMatches.length > 0 && (
              <div data-testid="gc-roi-quick-add-results" style={{ marginBottom: "0.55rem" }}>
                {quickAddMatches.map((match) => {
                  const selected = quickAddSelected?.vendorId === match.vendorId;
                  return (
                    <button
                      key={match.vendorId}
                      type="button"
                      style={{
                        ...S.searchResultBtn,
                        ...(selected ? S.searchResultBtnSelected : null),
                      }}
                      onClick={() => {
                        setQuickAddSelected(match);
                        setQuickAddError(null);
                      }}
                      aria-pressed={selected}
                    >
                      {match.companyName}
                      {(match.trancheLabels || []).length > 0
                        ? ` · ${(match.trancheLabels || []).join(", ")}`
                        : ""}
                    </button>
                  );
                })}
              </div>
            )}
            {quickAddSelected && (
              <p style={{ ...S.queueMeta, marginBottom: "0.45rem" }} data-testid="gc-roi-quick-add-selected">
                Selected: {quickAddSelected.companyName}
              </p>
            )}
            <label style={S.fieldLabel} htmlFor="gc-roi-quick-add-notes">
              Notes
            </label>
            <textarea
              id="gc-roi-quick-add-notes"
              aria-label="Quick add notes"
              value={quickAddNotes}
              onChange={(e) => setQuickAddNotes(e.target.value)}
              style={{ ...S.searchInput, minHeight: "4rem", resize: "vertical" as const }}
              placeholder="Optional internal note"
            />
            <div style={{ marginTop: "0.55rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                style={{ ...S.primaryBtn, opacity: quickAddSubmitting ? 0.45 : 1 }}
                onClick={handleQuickAdd}
                disabled={quickAddSubmitting}
              >
                {quickAddSubmitting ? "Adding..." : "Quick add to Introduction Queue"}
              </button>
            </div>
            {quickAddError && <p style={S.errorText} data-testid="gc-roi-quick-add-error">{quickAddError}</p>}
            {quickAddMessage && <p style={{ ...S.queueMeta, marginTop: "0.4rem" }}>{quickAddMessage}</p>}

            <div style={{ borderTop: "1px solid #25303d", marginTop: "0.85rem", paddingTop: "0.65rem" }} data-testid="gc-roi-source-pending-add">
              <button
                type="button"
                style={S.secondaryBtn}
                aria-expanded={pendingFormOpen}
                onClick={() => setPendingFormOpen((open) => !open)}
              >
                {pendingFormOpen ? "Hide source-pending form" : "Not in TradeTapp data yet? Add source-pending trade partner"}
              </button>
              {pendingFormOpen && (
                <div style={{ marginTop: "0.6rem" }}>
                  <p style={{ ...S.sectionCopy, marginBottom: "0.65rem" }}>
                    For a real trade partner the GC asked to introduce before its TradeTapp qualification is submitted. No TradeTapp ID, financial, readiness, or risk data is created; the row is linked to the TradeTapp record once it arrives.
                  </p>
                  {([
                    ["companyName", "Company name (exact, from source)", "text"],
                    ["primaryContactName", "Primary contact (optional)", "text"],
                    ["primaryContactEmail", "Primary email (optional)", "email"],
                    ["sourceEvidence", "Source evidence", "text"],
                  ] as const).map(([field, label, type]) => (
                    <div key={field} style={{ marginBottom: "0.5rem" }}>
                      <label style={S.fieldLabel} htmlFor={`gc-roi-pending-${field}`}>{label}</label>
                      <input
                        id={`gc-roi-pending-${field}`}
                        aria-label={label}
                        type={type}
                        value={pendingForm[field]}
                        onChange={(e) => setPendingForm((f) => ({ ...f, [field]: e.target.value }))}
                        style={S.searchInput}
                        placeholder={field === "sourceEvidence" ? "e.g. TradeTapp invitation; application not started" : ""}
                      />
                    </div>
                  ))}
                  <label style={S.fieldLabel} htmlFor="gc-roi-pending-notes">Notes</label>
                  <textarea
                    id="gc-roi-pending-notes"
                    aria-label="Source-pending notes"
                    value={pendingForm.notes}
                    onChange={(e) => setPendingForm((f) => ({ ...f, notes: e.target.value }))}
                    style={{ ...S.searchInput, minHeight: "4rem", resize: "vertical" as const }}
                    placeholder="Provenance / request context"
                  />
                  <div style={{ marginTop: "0.55rem" }}>
                    <button
                      type="button"
                      style={{ ...S.primaryBtn, opacity: pendingSubmitting ? 0.45 : 1 }}
                      onClick={handleAddSourcePending}
                      disabled={pendingSubmitting}
                    >
                      {pendingSubmitting ? "Adding..." : "Add source-pending trade partner"}
                    </button>
                  </div>
                  {pendingError && <p style={S.errorText} data-testid="gc-roi-source-pending-error">{pendingError}</p>}
                  {pendingMessage && <p style={{ ...S.queueMeta, marginTop: "0.4rem" }}>{pendingMessage}</p>}
                </div>
              )}
            </div>
          </div>

          {queueLoading && <p style={S.emptyQ}>Loading queue...</p>}
          {!queueLoading && queue.length === 0 && (
            <p style={S.emptyQ}>No vendors in queue yet. Click any metric tile to drill down and add vendors.</p>
          )}
          {!queueLoading && queue.length > 0 && filteredQueue.length === 0 && (
            <p style={S.emptyQ}>No queue rows match this company name filter.</p>
          )}
          {!queueLoading && filteredQueue.map((item) => {
            const stageLabel = item.stage_label || item.status;
            const pct = progressPercent(item);
            const outreach = Boolean(item.outreach_occurred);
            const manuallyAdded = Boolean(item.manuallyAdded);
            const sourcePending = item.source_pending === true;
            const createdByLabel = createdByDisplayLabel(item);
            return (
              <div key={item.id} style={S.queueRow} data-testid="gc-roi-queue-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={S.queueName}>
                    {item.company_name}
                    {manuallyAdded && (
                      <span style={S.manualBadge} data-testid="gc-roi-manually-added-badge">
                        Manually added
                      </span>
                    )}
                    {sourcePending && (
                      <span style={S.sourcePendingBadge} data-testid="gc-roi-source-pending-badge">
                        Source pending
                      </span>
                    )}
                  </p>
                  <p style={S.queueMeta}>
                    {pathwaySignal(item.metric_key)} {"\u00B7"} {projectContextLabel(item)} {"\u00B7"} {item.source_snapshot}
                  </p>
                  {sourcePending && (
                    <div style={S.sourcePendingPanel} data-testid="gc-roi-source-pending-details">
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Prequalification</span>
                        TradeTapp submission pending
                      </p>
                      <p style={S.queueField}>
                        <span style={S.queueLabel}>Financial / readiness evidence</span>
                        Not available yet {"\u2014"} no TradeTapp qualification or financial submission received
                      </p>
                      {item.source_evidence && (
                        <p style={S.queueField}>
                          <span style={S.queueLabel}>Source</span>
                          {item.source_evidence}
                        </p>
                      )}
                      {(item.primary_contact_name || item.primary_contact_email) && (
                        <p style={S.queueField}>
                          <span style={S.queueLabel}>Primary contact</span>
                          {[item.primary_contact_name, item.primary_contact_email].filter(Boolean).join(" \u00B7 ")}
                        </p>
                      )}
                    </div>
                  )}
                  <span style={S.statusBadge}>{stageLabel}</span>
                  <div style={S.queuePolicy}>
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Owner</span>
                      <span style={S.ownerPill}>{item.owner || "Unassigned"}</span>
                    </p>
                    {createdByLabel && (
                      <p style={S.queueField} data-testid="gc-roi-created-by">
                        <span style={S.queueLabel}>Created by</span>
                        {createdByLabel}
                      </p>
                    )}
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Outreach</span>
                      {outreachCopy(outreach)}
                    </p>
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Next action</span>
                      {item.next_action}
                    </p>
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Approval</span>
                      {approvalLabel(item)}
                    </p>
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Protect/Grow signal</span>
                      {pathwaySignal(item.metric_key)}
                    </p>
                    {timelineLabel(item) && (
                    <p style={S.queueField}>
                      <span style={S.queueLabel}>Timeline</span>
                      {timelineLabel(item)}
                    </p>
                    )}
                    <div style={S.queueField}>
                      <span style={S.queueLabel}>Progress</span>
                      {typeof item.progress_position === "number"
                        ? `Stage ${item.progress_position} of 7 \u00B7 ${pct}%`
                        : `${pct}%`}
                      <div style={S.progressTrack} data-testid="gc-roi-queue-progress" aria-hidden="true">
                        <div style={{ ...S.progressFill, width: `${Math.max(0, Math.min(100, pct))}%` }} />
                      </div>
                    </div>
                  </div>
                  <div style={S.queueNotes}>
                    <span style={S.queueLabel}>Notes</span>
                    {item.notes ? (
                      <p style={S.notePreview} data-testid="gc-roi-queue-notes">{notesPreview(item.notes)}</p>
                    ) : (
                      <span style={{ color: "#8d9aaa" }}>No note</span>
                    )}
                    {item.notes ? (
                      <button
                        type="button"
                        style={{ ...S.secondaryBtn, marginTop: "0.35rem" }}
                        onClick={() => setNotesDetailId(item.id)}
                      >
                        View notes
                      </button>
                    ) : editingNoteId === item.id ? (
                      <div style={{ marginTop: "0.4rem" }}>
                        <textarea
                          aria-label={`Note for ${item.company_name}`}
                          value={noteDraft}
                          onChange={(e) => setNoteDraft(e.target.value)}
                          style={{ ...S.searchInput, minHeight: "3.5rem", resize: "vertical" as const }}
                        />
                        <div style={{ display: "flex", gap: "0.4rem", marginTop: "0.35rem" }}>
                          <button
                            type="button"
                            style={S.primaryBtn}
                            disabled={noteSaving}
                            onClick={() => saveQueueNote(item)}
                          >
                            {noteSaving ? "Saving..." : "Save note"}
                          </button>
                          <button
                            type="button"
                            style={S.secondaryBtn}
                            onClick={() => setEditingNoteId(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        style={{ ...S.secondaryBtn, marginTop: "0.35rem" }}
                        onClick={() => startEditNote(item)}
                      >
                        Add note
                      </button>
                    )}
                  </div>
                </div>
                <div style={S.queueRight}>
                  <button type="button" style={S.primaryBtn} onClick={() => openActionModal(item)}>
                    {queuePrimaryLabel(item.workflow_stage)}
                  </button>
                  <button type="button" style={S.secondaryBtn} onClick={() => openTracker(item)}>
                    Track status
                  </button>
                  <button style={S.removeBtn} onClick={() => handleRemoveFromQueue(item.id)}>Remove</button>
                </div>
              </div>
            );
          })}
          <p style={S.sourceNote}>
            Queue insertion and GC approval do not mean outreach occurred. Outreach is shown only at the Introduction Sent milestone.
          </p>
        </section>
        )}
      </main>

      <GcRoiDrillDrawer
        isOpen={drawerOpen}
        metricKey={drawerKey}
        metricLabel={drawerLabel}
        onClose={() => setDrawerOpen(false)}
        onQueued={loadQueue}
      />

      {contextualRow && (
        <ContextualTradePartnerDrawer
          row={contextualRow}
          reason={contextualReason}
          onClose={() => setContextualRow(null)}
          onQueued={loadQueue}
        />
      )}

      {actionItem && (
        <GcRoiIntroductionActionModal
          item={actionItem}
          onClose={() => setActionItem(null)}
          onPrimaryAction={handlePrimaryAdvance}
          onViewWorkflow={(item) => {
            setActionItem(null);
            openTracker(item);
          }}
        />
      )}
      {trackerItem && (
        <GcRoiIntroductionWorkflowTracker
          item={trackerItem}
          onClose={() => setTrackerItem(null)}
        />
      )}
      {notesDetailId && queue.filter((item) => item.id === notesDetailId).map((notesDetail) => (
        <div
          key={notesDetail.id}
          className="gc-roi-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={`Notes for ${notesDetail.company_name}`}
          data-testid="gc-roi-notes-detail"
        >
          <div className="gc-roi-modal-card">
            <div className="gc-roi-modal-header">
              <div>
                <p className="gc-roi-modal-title">{notesDetail.company_name}</p>
                <p className="gc-roi-modal-subtitle">
                  Complete notes and history, in the order they were recorded.
                </p>
              </div>
              <button
                type="button"
                className="gc-roi-modal-close"
                aria-label="Close notes"
                onClick={() => {
                  setNotesDetailId(null);
                  setEditingNoteId(null);
                }}
              >
                {"\u00D7"}
              </button>
            </div>
            {editingNoteId === notesDetail.id ? (
              <div>
                <textarea
                  aria-label={`Note for ${notesDetail.company_name}`}
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  style={{ ...S.searchInput, minHeight: "12rem", resize: "vertical" as const, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                />
                <div className="gc-roi-modal-actions">
                  <button
                    type="button"
                    className="gc-roi-btn-secondary"
                    onClick={() => setEditingNoteId(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="gc-roi-btn-primary"
                    disabled={noteSaving}
                    onClick={() => saveQueueNote(notesDetail)}
                  >
                    {noteSaving ? "Saving..." : "Save note"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <pre className="gc-roi-notes-body" data-testid="gc-roi-notes-detail-body">{notesDetail.notes}</pre>
                <div className="gc-roi-modal-actions">
                  <button
                    type="button"
                    className="gc-roi-btn-secondary"
                    onClick={() => startEditNote(notesDetail)}
                  >
                    Edit note
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ))}

      <style>{`
        .gc-roi-modal-overlay{position:fixed;inset:0;background:rgba(2,6,23,.72);display:flex;align-items:flex-start;justify-content:center;padding:4vh 1rem 2rem;z-index:300;overflow:auto}
        .gc-roi-modal-card,.gc-roi-tracker-card{width:min(720px,100%);background:#10151d;border:1px solid #25303d;border-radius:14px;padding:1.15rem 1.25rem 1.25rem;box-shadow:0 24px 60px rgba(0,0,0,.45);color:#f2f5f8}
        .gc-roi-tracker-card{width:min(860px,100%)}
        .gc-roi-modal-header{display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;margin-bottom:1rem}
        .gc-roi-modal-title{font-size:1.15rem;font-weight:800;margin:0 0 .35rem}
        .gc-roi-modal-subtitle{color:#8d9aaa;font-size:.8rem;line-height:1.45;margin:0;max-width:36rem}
        .gc-roi-modal-close{background:none;border:1px solid #25303d;border-radius:.4rem;color:#8d9aaa;cursor:pointer;font-size:1.1rem;line-height:1;padding:.15rem .45rem}
        .gc-roi-modal-meta{display:grid;grid-template-columns:repeat(auto-fit,minmax(9.5rem,1fr));gap:.75rem;margin-bottom:1rem}
        .gc-roi-meta-label{color:#8d9aaa;font-size:.62rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;margin-bottom:.2rem}
        .gc-roi-meta-value{color:#e2e8f0;font-size:.82rem;font-weight:600}
        .gc-roi-meta-sub{color:#8d9aaa;font-size:.72rem;margin-top:.15rem}
        .gc-roi-status-pill{display:inline-block;border-radius:999px;font-size:.68rem;font-weight:700;padding:.28rem .65rem;background:#182231;color:#7eb6ff;border:1px solid #3a4b5e}
        .gc-roi-owner-pill{display:inline-block;border-radius:999px;font-size:.68rem;font-weight:700;padding:.22rem .55rem;background:#0b0f15;color:#e2e8f0;border:1px solid #3a4b5e}
        .gc-roi-language-block{white-space:pre-wrap;background:#0b0f15;border:1px solid #25303d;border-radius:10px;padding:.85rem;color:#d5dbe2;font-size:.78rem;line-height:1.5;margin:.35rem 0 0;font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}
        .gc-roi-notes-body{white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word;max-height:50vh;overflow:auto;background:#0b0f15;border:1px solid #25303d;border-radius:10px;padding:.9rem 1rem;color:#e2e8f0;font-size:.84rem;line-height:1.6;margin:0;font-family:inherit}
        .gc-roi-modal-actions{display:flex;flex-wrap:wrap;gap:.5rem;justify-content:flex-end;margin-top:1rem}
        .gc-roi-btn-primary{background:#1d4ed8;border:1px solid #1d4ed8;border-radius:.45rem;color:#f8fafc;cursor:pointer;font-size:.72rem;font-weight:700;padding:.5rem .75rem}
        .gc-roi-btn-secondary{background:#0b0f15;border:1px solid #25303d;border-radius:.45rem;color:#d5dbe2;cursor:pointer;font-size:.72rem;font-weight:700;padding:.5rem .75rem}
        .gc-roi-modal-stage-hint{color:#8d9aaa;font-size:.68rem;margin-top:.75rem}
        .gc-roi-governance{background:#0b0f15;border:1px solid #25303d;border-radius:10px;padding:.75rem .85rem;color:#8d9aaa;font-size:.78rem;line-height:1.45;margin-bottom:1rem}
        .gc-roi-progress-track{height:7px;border-radius:999px;background:#0b0f15;border:1px solid #25303d;overflow:hidden;margin:.35rem 0 .35rem}
        .gc-roi-progress-fill{height:100%;background:#7eb6ff}
        .gc-roi-tracker-summary{display:flex;justify-content:space-between;gap:1rem;align-items:flex-start;margin-bottom:.85rem;flex-wrap:wrap}
        .gc-roi-stage-row{list-style:none;display:flex;gap:0;padding:0;margin:0 0 1.1rem;overflow-x:auto}
        .gc-roi-stage-item{position:relative;flex:1 0 6.5rem;display:flex;flex-direction:column;align-items:center;text-align:center;padding:0 .25rem;min-width:5.5rem}
        .gc-roi-stage-circle{width:1.7rem;height:1.7rem;border-radius:999px;border:2px solid #3a4b5e;display:flex;align-items:center;justify-content:center;font-size:.7rem;font-weight:800;background:#0b0f15;color:#8d9aaa;z-index:1}
        .gc-roi-stage-label{margin-top:.4rem;font-size:.62rem;line-height:1.25;color:#8d9aaa;font-weight:700}
        .gc-roi-stage-connector{position:absolute;top:.8rem;right:50%;width:100%;height:2px;background:#25303d;z-index:0}
        .gc-roi-connector-done{background:#7eb6ff}
        .gc-roi-stage-completed .gc-roi-stage-circle{background:#1d4ed8;border-color:#7eb6ff;color:#fff}
        .gc-roi-stage-completed .gc-roi-stage-label{color:#d5dbe2}
        .gc-roi-stage-current .gc-roi-stage-circle{background:#1d4ed8;border-color:#7eb6ff;color:#fff;box-shadow:0 0 0 3px rgba(126,182,255,.25)}
        .gc-roi-stage-current .gc-roi-stage-label{color:#f2f5f8}
        .gc-roi-timeline{display:grid;gap:.55rem;margin-bottom:1rem}
        .gc-roi-timeline-card{background:#0b0f15;border:1px solid #25303d;border-radius:10px;padding:.7rem .8rem}
        .gc-roi-timeline-title{font-size:.78rem;font-weight:800;color:#e2e8f0;margin-bottom:.15rem}
        .gc-roi-timeline-detail{color:#8d9aaa;font-size:.72rem;line-height:1.4;margin-top:.25rem}
        .gc-roi-tracker-footer{display:grid;grid-template-columns:repeat(auto-fit,minmax(11rem,1fr));gap:.75rem;border-top:1px solid #25303d;padding-top:.9rem}
        @media (max-width:1050px){
          .gc-roi-modal-card,.gc-roi-tracker-card{padding:1rem}
        }
        @media (max-width:720px){
          .gc-roi-stage-item{flex:1 0 4.8rem}
        }
      `}</style>
    </div>
  );
}
